import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  ArrowLeft,
  Box,
  Building2,
  Calendar,
  Check,
  CheckCircle2,
  ChevronDown,
  ClipboardCheck,
  ClipboardList,
  Clock,
  CreditCard,
  Edit3,
  Filter,
  IndianRupee,
  Layers3,
  ListChecks,
  Loader2,
  Pause,
  Play,
  Plus,
  RefreshCcw,
  Search,
  Trash2,
  UserCheck,
  Users,
  X,
} from "lucide-react";
import { ProjectApi } from "api/projects/service";
import { useAlert } from "components/UI_components/Alert";
import { BASE_URL } from "utils/constant";
import Button from "components/ui-custom/Button";
import ConfirmationModal from "components/ConfirmationModal";

const ACTION_TYPES = [
  {
    value: "WORKER_ATTENDANCE",
    label: "Worker Attendance",
    description: "Daily workforce presence",
    icon: UserCheck,
    tone: "blue",
  },
  {
    value: "CONTRACTOR_ATTENDANCE",
    label: "Contractor Attendance",
    description: "Contractor presence check",
    icon: Building2,
    tone: "amber",
  },
  {
    value: "TASK_UPDATE",
    label: "Task Update",
    description: "Progress reporting",
    icon: ClipboardList,
    tone: "violet",
  },
  {
    value: "SUBTASK_UPDATE",
    label: "Subtask Update",
    description: "Subtask progress check",
    icon: ListChecks,
    tone: "violet",
  },
  // {
  //   value: "VENDOR_PAYMENT",
  //   label: "Vendor Payment",
  //   description: "Scheduled vendor payment",
  //   icon: CreditCard,
  //   tone: "green",
  // },
  // {
  //   value: "CONTRACTOR_PAYMENT",
  //   label: "Contractor Payment",
  //   description: "RA or milestone payment",
  //   icon: IndianRupee,
  //   tone: "green",
  // },
  // {
  //   value: "WORKER_PAYMENT",
  //   label: "Worker Payment",
  //   description: "Wage disbursement",
  //   icon: Users,
  //   tone: "green",
  // },
  {
    value: "INVENTORY_UPDATE",
    label: "Inventory Update",
    description: "Stock count or reorder",
    icon: Box,
    tone: "orange",
  },
  //   {
  //     value: "SITE_UPDATE",
  //     label: "Site Update",
  //     description: "Project status note",
  //     icon: FileText,
  //     tone: "slate",
  //   },
];

const ORGANIZATION_ACTION_TYPES = ["VENDOR_PAYMENT", "CONTRACTOR_PAYMENT"];

const getAvailableActionTypes = (scope) =>
  ACTION_TYPES.filter((item) =>
    scope === "PROJECT"
      ? item.value !== "VENDOR_PAYMENT"
      : ORGANIZATION_ACTION_TYPES.includes(item.value),
  );

const getDefaultActionType = (scope) =>
  getAvailableActionTypes(scope)[0]?.value || ACTION_TYPES[0].value;

const ACTION_PERMISSION_REQUIREMENTS = {
  WORKER_ATTENDANCE: {
    kind: "sitePermission",
    permission: "edit_worker_management",
    reason: "Needs worker management edit permission",
  },
  INVENTORY_UPDATE: {
    kind: "sitePermission",
    permission: "edit_material_management",
    reason: "Needs material management edit permission",
  },
  WORKER_PAYMENT: {
    kind: "sitePermission",
    permission: "edit_finance",
    reason: "Needs finance edit permission",
  },
  CONTRACTOR_PAYMENT: {
    kind: "sitePermission",
    permission: "edit_finance",
    reason: "Needs finance edit permission",
  },
  TASK_UPDATE: {
    kind: "assignmentCount",
    field: "task_assigned",
    reason: "No assigned tasks found",
  },
  SUBTASK_UPDATE: {
    kind: "assignmentCount",
    field: "subtask_assigned",
    reason: "No assigned subtasks found",
  },
};

const EMPTY_ACTION_PERMISSION_STATE = {
  loaded: false,
  loading: false,
  error: "",
  siteMember: null,
  assignedOverview: null,
};

const FREQUENCIES = [
  { value: "ONCE", label: "One-time" },
  { value: "DAILY", label: "Daily" },
  { value: "WEEKLY", label: "Weekly" },
  { value: "MONTHLY", label: "Monthly" },
  { value: "CUSTOM_DAYS", label: "Custom days" },
  // { value: "CUSTOM_DATE", label: "Custom date" },
];

const WEEKDAYS = [
  { value: "MON", label: "M", fullLabel: "Monday" },
  { value: "TUE", label: "T", fullLabel: "Tuesday" },
  { value: "WED", label: "W", fullLabel: "Wednesday" },
  { value: "THU", label: "T", fullLabel: "Thursday" },
  { value: "FRI", label: "F", fullLabel: "Friday" },
  { value: "SAT", label: "S", fullLabel: "Saturday" },
  { value: "SUN", label: "S", fullLabel: "Sunday" },
];

const EMPTY_FORM = {
  title: "",
  action_type: getDefaultActionType("PROJECT"),
  selected_actions: [],
  action_configs: {},
  reassignments: {},
  assignee: "",
  scope: "PROJECT",
  site: "",
  sites: [],
  frequency: "DAILY",
  start_date: getLocalDateKey(),
  custom_date: "",
  custom_days: [],
  due_time: "09:00",
  notes: "",
  status: "ACTIVE",
};

const getDefaultActionConfig = (seed = {}) => ({
  frequency: seed.frequency || "DAILY",
  start_date: seed.start_date || getLocalDateKey(),
  custom_date: seed.custom_date || "",
  custom_days: Array.isArray(seed.custom_days) ? seed.custom_days : [],
  due_time: String(seed.due_time || "09:00").slice(0, 5),
  notes: seed.notes || "",
  status: seed.status || "ACTIVE",
});

const getActionConfig = (form, actionType) =>
  form?.action_configs?.[actionType] ||
  getDefaultActionConfig({
    frequency: form?.frequency,
    start_date: form?.start_date,
    custom_date: form?.custom_date,
    custom_days: form?.custom_days,
    due_time: form?.due_time,
    notes: form?.notes,
    status: form?.status,
  });

const getSelectedActionTypes = (form) =>
  Array.isArray(form?.selected_actions)
    ? form.selected_actions
    : form?.action_type
      ? [form.action_type]
      : [];

const memberAccent = [
  "bg-blue-100 text-blue-700",
  "bg-green-100 text-green-700",
  "bg-violet-100 text-violet-700",
  "bg-amber-100 text-amber-700",
  "bg-rose-100 text-rose-700",
  "bg-slate-100 text-slate-700",
];

const getAuthHeaders = () => {
  const accessToken = localStorage.getItem("access");
  return {
    "Content-Type": "application/json",
    ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
  };
};

const getOrganizationId = (organizationId) => {
  if (organizationId) return organizationId;
  try {
    return JSON.parse(localStorage.getItem("organization"))?.id;
  } catch (_) {
    return null;
  }
};

function getLocalDateKey() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

const normalizeList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const normalizeAssignedOverview = (payload) => {
  if (payload?.data && !Array.isArray(payload.data)) return payload.data;
  if (payload && !Array.isArray(payload)) return payload;
  return {};
};

const normalizeMember = (member, index) => {
  const user = member?.user || {};
  const firstName = user?.first_name || "";
  const lastName = user?.last_name || "";
  const fullName =
    [firstName, lastName].filter(Boolean).join(" ").trim() ||
    user?.email ||
    "Team member";
  const initials =
    `${firstName?.charAt(0) || ""}${lastName?.charAt(0) || ""}`.trim() ||
    fullName
      .split(" ")
      .map((part) => part.charAt(0))
      .slice(0, 2)
      .join("")
      .toUpperCase() ||
    "TM";

  return {
    ...member,
    displayName: fullName,
    initials,
    userpic: user?.userpic || null,
    role:
      member?.joined_as ||
      member?.role ||
      (member?.is_admin ? "Admin" : "Member"),
    accentClass: memberAccent[index % memberAccent.length],
  };
};

const normalizeSite = (site) => ({
  ...site,
  displayName:
    site?.site_name ||
    site?.name ||
    site?.project_name ||
    `Project ${site?.id || ""}`.trim(),
});

const formatActionLabel = (value) =>
  ACTION_TYPES.find((item) => item.value === value)?.label ||
  String(value || "")
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const getActionMeta = (value) =>
  ACTION_TYPES.find((item) => item.value === value) || ACTION_TYPES[0];

const hasSitePermission = (siteMember, permission) =>
  Boolean(
    siteMember?.[permission] ||
    siteMember?.edit_all ||
    siteMember?.is_admin ||
    siteMember?.org_admin,
  );

const getActionPermissionStatus = (actionType, form, permissionState) => {
  if (form?.scope !== "PROJECT") return { allowed: true, reason: "" };

  const requirement = ACTION_PERMISSION_REQUIREMENTS[actionType];
  if (!requirement) return { allowed: true, reason: "" };

  if (permissionState?.loading) {
    return { allowed: false, reason: "Checking permissions..." };
  }

  if (!permissionState?.loaded) {
    return { allowed: false, reason: "Checking permissions..." };
  }

  if (permissionState?.error) {
    return { allowed: false, reason: permissionState.error };
  }

  if (!permissionState?.siteMember) {
    return { allowed: false, reason: "Member is not assigned to this site" };
  }

  if (requirement.kind === "sitePermission") {
    return hasSitePermission(
      permissionState?.siteMember,
      requirement.permission,
    )
      ? { allowed: true, reason: "" }
      : { allowed: false, reason: requirement.reason };
  }

  if (requirement.kind === "assignmentCount") {
    const assignedCount = Number(
      permissionState?.assignedOverview?.[requirement.field] || 0,
    );
    return assignedCount > 0
      ? { allowed: true, reason: "" }
      : { allowed: false, reason: requirement.reason };
  }

  return { allowed: true, reason: "" };
};

const isOwnerMember = (member) => {
  const roleText = [
    member?.joined_as,
    member?.role,
    member?.profession,
    member?.user?.profession,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return roleText.includes("owner");
};

const getSelectedSiteIds = (form) => {
  if (Array.isArray(form?.sites) && form.sites.length)
    return form.sites.map(String);
  return form?.site ? [String(form.site)] : [];
};

const getTimeParts = (time) => {
  const [hourValue = "09", minuteValue = "00"] = String(time || "09:00").split(
    ":",
  );
  const hour24 = Number(hourValue);
  const validHour = Number.isNaN(hour24)
    ? 9
    : Math.max(0, Math.min(23, hour24));
  const minute = Number(minuteValue);
  const validMinute = Number.isNaN(minute)
    ? 0
    : Math.max(0, Math.min(59, minute));
  const hour12 = validHour % 12 || 12;

  return {
    hour: String(hour12).padStart(2, "0"),
    minute: String(validMinute).padStart(2, "0"),
    period: validHour >= 12 ? "PM" : "AM",
  };
};

const toFormTime = ({ hour, minute, period }) => {
  let hourValue = Number(hour);
  if (period === "PM" && hourValue < 12) hourValue += 12;
  if (period === "AM" && hourValue === 12) hourValue = 0;
  return `${String(hourValue).padStart(2, "0")}:${String(Number(minute)).padStart(2, "0")}`;
};

const toDisplayTime = (time) => {
  if (!time) return "No time";
  const [hourValue, minuteValue] = String(time).split(":");
  const date = new Date();
  date.setHours(Number(hourValue || 0), Number(minuteValue || 0), 0, 0);
  return date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
};

const toCompactDisplayTime = (time) => {
  const [hourStr, minuteStr] = time.split(":");
  let hours = parseInt(hourStr, 10);
  const minutes = minuteStr;
  const period = hours >= 12 ? "PM" : "AM";

  hours = hours % 12 || 12;

  return minutes === "00"
    ? `${hours} ${period}`
    : `${hours}:${minutes} ${period}`;
};

const toApiTime = (time) => {
  if (!time) return null;
  const [hours = "00", minutes = "00", seconds = "00"] =
    String(time).split(":");
  return `${hours.padStart(2, "0")}:${minutes.padStart(2, "0")}:${seconds.padStart(2, "0")}`;
};

const formatDate = (date) => {
  if (!date) return "Not set";
  const parsed = new Date(`${date}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return date;
  return parsed.toLocaleDateString([], {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const getOrdinalDay = (day) => {
  const value = Number(day);
  if ([11, 12, 13].includes(value % 100)) return `${value}th`;
  const suffix = { 1: "st", 2: "nd", 3: "rd" }[value % 10] || "th";
  return `${value}${suffix}`;
};

const formatDueDateLabel = (dateKey, todayKey = getLocalDateKey()) => {
  if (!dateKey) return "Not scheduled";
  const diff = getDateDiffInDays(todayKey, dateKey);
  if (diff === 0) return "Today";
  if (diff === 1) return "Tomorrow";
  const parsed = new Date(`${dateKey}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return formatDate(dateKey);
  if (diff !== null && diff > 1 && diff < 7) {
    return parsed.toLocaleDateString([], { weekday: "long" });
  }
  const month = parsed.toLocaleDateString([], { month: "long" });
  return `${getOrdinalDay(parsed.getDate())} ${month}`;
};

// 12 hr format
const formatDateToTime = (date) => {
  if (!date) return "Not set";
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return date;
  return parsed.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const parseDateKeyUtc = (dateKey) => {
  if (!dateKey) return null;
  const [year, month, day] = String(dateKey)
    .slice(0, 10)
    .split("-")
    .map(Number);
  if (!year || !month || !day) return null;
  return Date.UTC(year, month - 1, day);
};

const getDateDiffInDays = (fromDateKey, toDateKey) => {
  const fromTime = parseDateKeyUtc(fromDateKey);
  const toTime = parseDateKeyUtc(toDateKey);
  if (fromTime === null || toTime === null) return null;
  return Math.floor((toTime - fromTime) / 86400000);
};

const getWeekdayKey = (dateKey) => {
  const parsed = new Date(`${dateKey}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toLocaleDateString("en-US", { weekday: "short" }).toUpperCase();
};

const getDateForWeekdayOnOrAfter = (dateKey, weekday) => {
  const parsed = new Date(`${dateKey || getLocalDateKey()}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return dateKey || getLocalDateKey();
  const targetIndex = WEEKDAYS.findIndex((day) => day.value === weekday);
  if (targetIndex < 0) return dateKey || getLocalDateKey();
  const currentIndex = (parsed.getDay() + 6) % 7;
  const daysToAdd = (targetIndex - currentIndex + 7) % 7;
  parsed.setDate(parsed.getDate() + daysToAdd);
  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, "0");
  const day = String(parsed.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getWeeklyDay = (config = {}) =>
  config.custom_days?.[0] ||
  getWeekdayKey(config.start_date || getLocalDateKey());

const hasStartedByDate = (reminder, dateKey) => {
  if (!reminder?.start_date) return true;
  return reminder.start_date <= dateKey;
};

const isReminderScheduledForDate = (reminder, dateKey) => {
  if (!reminder || !dateKey || reminder.status !== "ACTIVE") return false;
  const frequency = reminder.frequency;
  const startDate = reminder.start_date;

  if (frequency === "DAILY") {
    return Boolean(startDate) && startDate <= dateKey;
  }

  if (frequency === "WEEKLY") {
    const weeklyDays = (reminder.custom_days || []).map((day) =>
      String(day).slice(0, 3).toUpperCase(),
    );
    if (weeklyDays.length) {
      return (
        hasStartedByDate(reminder, dateKey) &&
        weeklyDays.includes(getWeekdayKey(dateKey))
      );
    }
    const diffInDays = getDateDiffInDays(startDate, dateKey);
    return diffInDays !== null && diffInDays >= 0 && diffInDays % 7 === 0;
  }

  if (frequency === "CUSTOM_DAYS") {
    const todayKey = getWeekdayKey(dateKey);
    const customDays = (reminder.custom_days || []).map((day) =>
      String(day).slice(0, 3).toUpperCase(),
    );
    return hasStartedByDate(reminder, dateKey) && customDays.includes(todayKey);
  }

  if (frequency === "MONTHLY") {
    const seedDateKey = reminder.custom_date || reminder.start_date;
    const seedDay = Number(String(seedDateKey || "").slice(8, 10));
    const dateDay = Number(String(dateKey || "").slice(8, 10));
    return hasStartedByDate(reminder, dateKey) && seedDay === dateDay;
  }

  if (frequency === "ONCE" || frequency === "CUSTOM_DATE") {
    return reminder.custom_date === dateKey;
  }

  return false;
};

const isReminderDueToday = (reminder) =>
  isReminderScheduledForDate(reminder, getLocalDateKey());

const isDueTimePassedForDate = (reminder, dateKey) => {
  if (!reminder?.due_time || !dateKey) return false;
  const todayKey = getLocalDateKey();
  if (dateKey < todayKey) return true;
  if (dateKey > todayKey) return false;
  const [hours, minutes] = String(reminder.due_time).split(":");
  const due = new Date();
  due.setHours(Number(hours || 0), Number(minutes || 0), 0, 0);
  return due.getTime() < Date.now();
};

const getNormalizedCustomDays = (reminder) =>
  (reminder?.custom_days || []).map((day) =>
    String(day).slice(0, 3).toUpperCase(),
  );

const getDateKeyFromDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const addMonthsClamped = (date, monthCount) => {
  const next = new Date(date);
  const day = next.getDate();
  next.setDate(1);
  next.setMonth(next.getMonth() + monthCount);
  const lastDay = new Date(
    next.getFullYear(),
    next.getMonth() + 1,
    0,
  ).getDate();
  next.setDate(Math.min(day, lastDay));
  return next;
};

const getNextMonthlyOccurrenceDateKey = (reminder, todayKey) => {
  const seedDateKey = reminder?.custom_date || reminder?.start_date;
  if (!seedDateKey) return null;
  const seed = new Date(`${seedDateKey}T00:00:00`);
  if (Number.isNaN(seed.getTime())) return null;
  for (let index = 0; index < 36; index += 1) {
    const candidateKey = getDateKeyFromDate(addMonthsClamped(seed, index));
    if (candidateKey >= todayKey) return candidateKey;
  }
  return null;
};

const getNextRecurringOccurrenceDateKey = (reminder, todayKey) => {
  const startKey = reminder?.start_date || todayKey;
  for (let offset = 0; offset <= 370; offset += 1) {
    const date = new Date(`${todayKey}T00:00:00`);
    date.setDate(date.getDate() + offset);
    const dateKey = getDateKeyFromDate(date);
    if (dateKey < startKey) continue;
    if (
      isReminderScheduledForDate({ ...reminder, status: "ACTIVE" }, dateKey)
    ) {
      return dateKey;
    }
  }
  return null;
};

const getNextDueDateKey = (reminder, todayKey = getLocalDateKey()) => {
  if (!reminder) return null;
  const frequency = reminder.frequency;

  if (frequency === "DAILY") {
    return reminder.start_date && reminder.start_date > todayKey
      ? reminder.start_date
      : todayKey;
  }

  if (frequency === "ONCE" || frequency === "CUSTOM_DATE") {
    return reminder.custom_date || reminder.start_date || null;
  }

  if (frequency === "MONTHLY") {
    return getNextMonthlyOccurrenceDateKey(reminder, todayKey);
  }

  if (frequency === "WEEKLY" || frequency === "CUSTOM_DAYS") {
    return getNextRecurringOccurrenceDateKey(reminder, todayKey);
  }

  return reminder.start_date || todayKey;
};

const getNextDueLabel = (reminder, todayKey = getLocalDateKey()) => {
  if (reminder?.status === "CLOSED") return "Closed";
  if (reminder?.status === "PAUSED") return "Paused";
  const dateKey = getNextDueDateKey(reminder, todayKey);
  if (!dateKey) return `Due ${toCompactDisplayTime(reminder?.due_time)}`;
  return `${formatDueDateLabel(dateKey, todayKey)} ${toCompactDisplayTime(reminder?.due_time)}`;
};

const getLastCompletionDateKey = (reminder) =>
  String(reminder?.last_completion?.occurrence_date || "").slice(0, 10);

const buildPayload = (
  form,
  siteIdOverride,
  actionTypeOverride,
  configOverride,
) => {
  const isGlobal = form.scope === "GLOBAL";
  const actionType = actionTypeOverride || form.action_type;
  const config = configOverride || getActionConfig(form, actionType);
  const isCustomDate =
    config.frequency === "CUSTOM_DATE" || config.frequency === "ONCE";
  const weeklyDay = config.frequency === "WEEKLY" ? getWeeklyDay(config) : "";
  const startDate =
    config.frequency === "WEEKLY"
      ? getDateForWeekdayOnOrAfter(config.start_date, weeklyDay)
      : isCustomDate
        ? config.custom_date
        : config.start_date;
  const payload = {
    title: formatActionLabel(actionType),
    action_type: actionType,
    assignee: Number(form.assignee),
    scope: form.scope,
    frequency: config.frequency,
    start_date: startDate,
    custom_date: isCustomDate ? config.custom_date : null,
    custom_days:
      config.frequency === "WEEKLY"
        ? [weeklyDay]
        : config.frequency === "CUSTOM_DAYS"
          ? config.custom_days
          : [],
    due_time: toApiTime(config.due_time),
    notes: String(config.notes || "").trim(),
    status: config.status,
  };

  if (!isGlobal) {
    payload.site = Number(siteIdOverride || getSelectedSiteIds(form)[0]);
  }

  return payload;
};

const getReminderPlaceKey = (reminder) =>
  reminder?.scope === "GLOBAL" ? "GLOBAL" : String(reminder?.site || "");

const getFormPlaceKey = (form) =>
  form?.scope === "GLOBAL"
    ? "GLOBAL"
    : String(getSelectedSiteIds(form)[0] || "");

const isReminderInSelectedPlace = (reminder, form) =>
  String(reminder?.scope || "GLOBAL") === String(form?.scope || "GLOBAL") &&
  getReminderPlaceKey(reminder) === getFormPlaceKey(form);

const findExistingActionReminder = (reminders, form, actionType) =>
  reminders.find(
    (reminder) =>
      reminder?.status !== "CLOSED" &&
      reminder?.action_type === actionType &&
      isReminderInSelectedPlace(reminder, form),
  );

const statusMeta = {
  on_time: {
    label: "On time",
    dot: "bg-emerald-500",
    text: "text-emerald-700",
    pill: "bg-emerald-50 text-emerald-700",
  },
  delayed: {
    label: "Delayed",
    dot: "bg-amber-500",
    text: "text-amber-700",
    pill: "bg-amber-50 text-amber-700",
  },
  overdue: {
    label: "Overdue",
    dot: "bg-red-500",
    text: "text-red-700",
    pill: "bg-red-50 text-red-700",
  },
  upcoming: {
    label: "Upcoming",
    dot: "bg-violet-500",
    text: "text-violet-700",
    pill: "bg-violet-50 text-violet-700",
  },
  paused: {
    label: "Paused",
    dot: "bg-slate-400",
    text: "text-slate-700",
    pill: "bg-slate-100 text-slate-700",
  },
  closed: {
    label: "Closed",
    dot: "bg-slate-400",
    text: "text-slate-700",
    pill: "bg-slate-100 text-slate-700",
  },
};

const projectPalette = [
  "bg-violet-100 text-violet-700",
  "bg-amber-100 text-amber-700",
  "bg-red-100 text-red-700",
  "bg-emerald-100 text-emerald-700",
];

const getCompletionForDate = (reminder, dateKey) => {
  if (reminder?.completion?.occurrence_date === dateKey)
    return reminder.completion;
  if (getLastCompletionDateKey(reminder) === dateKey)
    return reminder.last_completion;
  return null;
};

const getOccurrenceStatus = (reminder, dateKey) => {
  const completion = getCompletionForDate(reminder, dateKey);
  if (completion) {
    const due = new Date(
      `${dateKey}T${toApiTime(reminder?.due_time) || "00:00:00"}`,
    );
    const completedAt = new Date(completion.completed_at);
    if (
      !Number.isNaN(due.getTime()) &&
      !Number.isNaN(completedAt.getTime()) &&
      completedAt.getTime() > due.getTime()
    ) {
      return "delayed";
    }
    return "on_time";
  }
  return isDueTimePassedForDate(reminder, dateKey) ? "overdue" : "upcoming";
};

const getMemberName = (reminder, members = []) =>
  members.find(
    (member) => String(member.id) === String(getReminderAssigneeId(reminder)),
  )?.displayName ||
  reminder?.assignee_name ||
  "Team member";

const getMemberRole = (member) =>
  member?.role ||
  member?.joined_as ||
  member?.profession ||
  member?.user?.profession ||
  "Team member";

const getInitials = (name) =>
  String(name || "TM")
    .split(" ")
    .filter(Boolean)
    .map((part) => part.charAt(0))
    .slice(0, 2)
    .join("")
    .toUpperCase() || "TM";

const getProjectName = (reminder) =>
  reminder?.site_name ||
  (reminder?.scope === "GLOBAL" ? "Organization" : "Project");

const getProjectCode = (name, id) => {
  const compact = String(name || "ORG")
    .replace(/[^a-zA-Z0-9\s]/g, "")
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part.slice(0, 3))
    .join("")
    .slice(0, 3)
    .toUpperCase();
  return `${compact || "PRJ"}-${String(id || 1)
    .padStart(2, "0")
    .slice(-2)}`;
};

const getReminderAssigneeId = (reminder) => {
  const assignee = reminder?.assignee;
  if (assignee && typeof assignee === "object") {
    return assignee.id || assignee.member_id || assignee.user?.id || null;
  }
  return (
    assignee ||
    reminder?.assignee_id ||
    reminder?.member ||
    reminder?.member_id ||
    null
  );
};

const normalizeReminderAssignee = (reminder) => ({
  ...reminder,
  assignee: getReminderAssigneeId(reminder) || reminder?.assignee,
});

const getDetailStatus = (item) => {
  if (item?.status === "PAUSED") return "paused";
  if (item?.status === "CLOSED") return "closed";
  return item?.occurrenceStatus || "upcoming";
};

const getTimeDistanceLabel = (reminder, dateKey) => {
  const now = new Date();
  const due = new Date(
    `${dateKey}T${toApiTime(reminder?.due_time) || "00:00:00"}`,
  );
  if (Number.isNaN(due.getTime())) return toDisplayTime(reminder?.due_time);
  const diff = Math.abs(due.getTime() - now.getTime());
  const hours = Math.max(1, Math.ceil(diff / 3600000));
  return hours >= 24 ? `${Math.ceil(hours / 24)}d` : `${hours}h`;
};

const buildOperationalRows = (reminders = [], members = [], dateKey) =>
  reminders.map((reminder) => {
    const assigneeId = getReminderAssigneeId(reminder);
    const member = members.find(
      (item) => String(item.id) === String(assigneeId),
    );
    const completion = getCompletionForDate(reminder, dateKey);
    const occurrenceStatus = getOccurrenceStatus(reminder, dateKey);
    return {
      ...reminder,
      assignee: assigneeId || reminder.assignee,
      member,
      memberName: getMemberName(reminder, members),
      memberRole: getMemberRole(member),
      memberInitials:
        member?.initials || getInitials(getMemberName(reminder, members)),
      projectName: getProjectName(reminder),
      completion,
      occurrenceStatus,
      submittedAt: completion?.completed_at || null,
    };
  });

const filterOperationalRows = (
  rows,
  { projectFilter, assigneeFilter, typeFilter, statusFilter },
) =>
  rows.filter((item) => {
    const matchesProject =
      projectFilter === "ALL" ||
      String(item.site || "GLOBAL") === String(projectFilter);
    const matchesAssignee =
      assigneeFilter === "ALL" ||
      String(getReminderAssigneeId(item)) === String(assigneeFilter);
    const matchesType = typeFilter === "ALL" || item.action_type === typeFilter;
    const matchesStatus =
      statusFilter === "ALL" ||
      item.occurrenceStatus === statusFilter ||
      getDetailStatus(item) === statusFilter;
    return matchesProject && matchesAssignee && matchesType && matchesStatus;
  });

const buildEmployeeOverview = (rows = [], members = []) => {
  const byAssignee = new Map();
  rows.forEach((item) => {
    const key = String(item.assignee || item.memberName);
    const current = byAssignee.get(key) || {
      id: item.assignee || key,
      name: item.memberName,
      userpic: item.member?.userpic || null,
      role: item.memberRole,
      initials: item.memberInitials,
      total: 0,
      active: 0,
      overdue: 0,
      onTime: 0,
      projects: new Map(),
      actions: [],
    };
    current.total += 1;
    if (
      item.occurrenceStatus !== "on_time" &&
      item.occurrenceStatus !== "delayed"
    )
      current.active += 1;
    if (item.occurrenceStatus === "overdue") current.overdue += 1;
    if (item.occurrenceStatus === "on_time") current.onTime += 1;
    const projectKey = String(item.site || "GLOBAL");
    const project = current.projects.get(projectKey) || {
      id: item.site || "GLOBAL",
      name: item.projectName,
      actions: [],
    };
    if (!project.actions.some((action) => action.value === item.action_type)) {
      project.actions.push({
        value: item.action_type,
        label: formatActionLabel(item.action_type),
        status: item.occurrenceStatus,
      });
    }
    current.projects.set(projectKey, project);
    current.actions.push(item);
    byAssignee.set(key, current);
  });

  members.forEach((member) => {
    const key = String(member.id);
    if (!byAssignee.has(key) && !isOwnerMember(member)) {
      byAssignee.set(key, {
        id: member.id,
        name: member.displayName,
        userpic: member.userpic,
        role: getMemberRole(member),
        initials: member.initials,
        total: 0,
        active: 0,
        overdue: 0,
        onTime: 0,
        projects: new Map(),
        actions: [],
      });
    }
  });

  return Array.from(byAssignee.values())
    .map((employee) => ({
      ...employee,
      projectCount: employee.projects.size,
      projects: Array.from(employee.projects.values()).slice(0, 2),
      onTimePercent: employee.total
        ? Math.round((employee.onTime / employee.total) * 100)
        : 0,
    }))
    .sort((a, b) => b.total - a.total);
};

function StatCard({ icon: Icon, value, label, colorClass }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
      <div className="flex items-center gap-4">
        <div
          className={`flex h-4 w-4 items-center justify-center rounded-lg ${colorClass}`}
        >
          <Icon size={16} />
        </div>
        <div>
          <p className="text-2xl font-semibold leading-none text-slate-900">
            {value}
          </p>
          <p className="mt-1 text-xs font-medium text-slate-500">{label}</p>
        </div>
      </div>
    </div>
  );
}

function DashboardSelect({
  icon: Icon,
  value,
  onChange,
  children,
  className = "",
}) {
  return (
    <label
      className={`relative inline-flex items-center rounded-lg border border-slate-200 bg-white p-2 ${className}`}
    >
      <Icon size={15} className="mr-3 shrink-0 text-slate-500" />
      <select
        value={value}
        onChange={onChange}
        className="min-w-0 flex-1 appearance-none bg-transparent pr-4 text-sm font-medium text-slate-700 outline-none"
      >
        {children}
      </select>
      <ChevronDown
        size={16}
        className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-slate-500"
      />
    </label>
  );
}

function StatusBadge({ status }) {
  const meta = statusMeta[status] || statusMeta.upcoming;
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold ${meta.pill}`}
    >
      <span className={`h-2 w-2 rounded-full ${meta.dot}`} />
      {meta.label}
    </span>
  );
}

function WeekdayChips({ days = [], className = "" }) {
  const selectedDays = new Set(
    days.map((day) => String(day).slice(0, 3).toUpperCase()),
  );

  if (selectedDays.size === 0) return null;

  return (
    <div className={`flex flex-wrap gap-2 ${className}`}>
      {WEEKDAYS.map((day) => {
        const selected = selectedDays.has(day.value);
        return (
          <span
            key={day.value}
            title={day.fullLabel}
            className={`flex h-8 w-8 items-center justify-center rounded-full border text-xs font-bold ${
              selected
                ? "border-violet-800 bg-violet-800 text-white"
                : "border-slate-300 bg-white text-slate-700"
            }`}
          >
            {day.label}
          </span>
        );
      })}
    </div>
  );
}

function MetricChip({ color = "emerald", label, value }) {
  const colors = {
    emerald: "bg-emerald-50 text-emerald-700",
    amber: "bg-amber-50 text-amber-700",
    red: "bg-red-50 text-red-700",
    violet: "bg-violet-50 text-violet-700",
  };
  const dots = {
    emerald: "bg-emerald-500",
    amber: "bg-amber-500",
    red: "bg-red-500",
    violet: "bg-violet-500",
  };
  return (
    <span
      className={`inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-[11px] font-semibold ${colors[color] || colors.emerald}`}
    >
      <span
        className={`h-1.5 w-1.5 rounded-full ${dots[color] || dots.emerald}`}
      />
      <span className="text-slate-500">{label}</span>
      <span className="text-slate-900">{value}</span>
    </span>
  );
}

function OperationalFeed({ rows, loading, selectedDate }) {
  const feedRows = [...rows].sort((a, b) => {
    const aTime = new Date(
      a.submittedAt || `${selectedDate}T${a.due_time || "00:00:00"}`,
    ).getTime();
    const bTime = new Date(
      b.submittedAt || `${selectedDate}T${b.due_time || "00:00:00"}`,
    ).getTime();
    return bTime - aTime;
  });

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4 col-span-2 shadow-sm">
      <div className="mb-3 flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold text-slate-950">
            Daily operational feed
          </h2>
          <p className="text-sm font-medium text-slate-500">
            Live submissions across every project
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex h-72 items-center justify-center">
          <Loader2 className="animate-spin text-sm-accent" size={30} />
        </div>
      ) : feedRows.length === 0 ? (
        <div className="flex h-72 flex-col items-center justify-center rounded-xl border border-dashed border-slate-200 text-center">
          <ClipboardCheck className="mb-3 text-slate-300" size={36} />
          <h3 className="text-base font-bold text-slate-900">
            No reminders for this date
          </h3>
          <p className="mt-1 text-sm font-medium text-slate-500">
            Change filters or create a schedule to populate the feed.
          </p>
        </div>
      ) : (
        <div className="relative space-y-4 pl-8 before:absolute before:left-1.5 before:top-2 before:h-[calc(100%-16px)] before:w-px before:bg-slate-200">
          {feedRows.map((item) => {
            const meta =
              item.occurrenceStatus === "on_time" ||
              item.occurrenceStatus === "delayed"
                ? statusMeta.on_time
                : statusMeta.upcoming;
            const completion = item.completion;
            return (
              <article
                key={`${item.id}-${item.occurrenceStatus}`}
                className="relative rounded-xl border border-slate-200 bg-white p-3 shadow-sm"
              >
                <span
                  className={`absolute -left-[34px] top-6 h-4 w-4 rounded-full border-3 border-white ${meta.dot}`}
                />
                <div className="flex flex-row items-center justify-between">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-[15px] font-semibold text-slate-800">
                      {formatActionLabel(item.action_type)}
                    </h3>
                    <span className="text-slate-300">·</span>
                    <p className="text-[15px] font-medium tracking-wide text-slate-800">
                      {item.projectName}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <StatusBadge status={item.occurrenceStatus} />
                    <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-500">
                      {item.frequency?.replace("_", " ").toLowerCase() ||
                        "scheduled"}
                    </span>
                  </div>
                </div>
                <p className="mt-2 text-sm font-medium text-slate-500">
                  {completion ? "Submitted by" : "Assigned to"}{" "}
                  <span className="font-semibold text-slate-800">
                    {item.memberName}
                  </span>
                  <span> · </span>
                  {formatDate(selectedDate)}
                  <span> · </span>
                  {`Due ${toDisplayTime(item.due_time)}`}
                </p>
                {item.frequency === "CUSTOM_DAYS" && (
                  <WeekdayChips
                    days={getNormalizedCustomDays(item)}
                    className="mt-3"
                  />
                )}
                <div className="mt-3 flex flex-wrap gap-2">
                  {completion ? (
                    <>
                      <MetricChip
                        color={"emerald"}
                        label="Progress"
                        value={`${completion.progress_completed || 0}/${completion.progress_total || 0}`}
                      />
                      <MetricChip
                        color="violet"
                        label="Submitted At"
                        value={formatDateToTime(completion.completed_at)}
                      />
                    </>
                  ) : (
                    <>
                      <MetricChip
                        color={
                          item.occurrenceStatus === "overdue" ? "red" : "violet"
                        }
                        label="Due"
                        value={toDisplayTime(item.due_time)}
                      />
                      <MetricChip
                        color="amber"
                        label="Status"
                        value={
                          statusMeta[item.occurrenceStatus]?.label || "Pending"
                        }
                      />
                    </>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}

function InsightList({ title, count, color, children }) {
  const dot =
    statusMeta[color]?.dot ||
    (color === "amber" ? "bg-amber-500" : "bg-violet-500");
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className={`h-2 w-2 rounded-full ${dot}`} />
          <h3 className="text-base font-bold text-slate-950">{title}</h3>
        </div>
        <span className="text-xs font-semibold text-slate-500">{count}</span>
      </div>
      <div className="divide-y divide-slate-100">{children}</div>
    </section>
  );
}

function SideInsights({ rows, selectedDate }) {
  const overdueRows = rows
    .filter((item) => item.occurrenceStatus === "overdue")
    .slice(0, 3);
  const missingRows = rows
    .filter((item) => !item.completion && item.occurrenceStatus !== "overdue")
    .slice(0, 3);
  const projects = Array.from(
    rows
      .reduce((map, item) => {
        const key = String(item.site || "GLOBAL");
        const current = map.get(key) || {
          id: key,
          name: item.projectName,
          count: 0,
        };
        current.count += 1;
        map.set(key, current);
        return map;
      }, new Map())
      .values(),
  )
    .sort((a, b) => b.count - a.count)
    .slice(0, 3);

  return (
    <div className="space-y-5 col-span-1">
      <InsightList
        title="Overdue actions"
        count={
          rows.filter((item) => item.occurrenceStatus === "overdue").length
        }
        color="overdue"
      >
        {overdueRows.length ? (
          overdueRows.map((item) => (
            <div
              key={`overdue-${item.id}`}
              className="flex items-center gap-3 py-2"
            >
              {item.member.userpic ? (
                <img
                  src={item.member.userpic}
                  alt={`${item.memberName}'s profile`}
                  className="h-9 w-9 shrink-0 rounded-full"
                  style={{ objectFit: "cover" }}
                />
              ) : (
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-violet-700 text-xs font-bold text-white">
                  {item.memberInitials}
                </span>
              )}
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-bold text-slate-950">
                  {formatActionLabel(item.action_type)}
                </p>
                <p className="truncate text-xs font-medium text-slate-500">
                  {item.memberName} · {item.projectName}
                </p>
              </div>
              <span className="rounded-full bg-red-50 px-3 py-1 text-xs font-bold text-red-600">
                ● {getTimeDistanceLabel(item, selectedDate)}
              </span>
            </div>
          ))
        ) : (
          <p className="py-5 text-sm font-medium text-slate-500">
            No overdue actions.
          </p>
        )}
      </InsightList>

      <InsightList
        title="Missing submissions"
        count={rows.filter((item) => !item.completion).length}
        color="amber"
      >
        {missingRows.length ? (
          missingRows.map((item) => (
            <div
              key={`missing-${item.id}`}
              className="flex items-center gap-3 py-3"
            >
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100 text-amber-700">
                <Clock size={18} />
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-bold text-slate-950">
                  {formatActionLabel(item.action_type)}
                </p>
                <p className="truncate text-xs font-medium text-slate-500">
                  {item.projectName}
                </p>
              </div>
              <span className="text-xs font-semibold text-slate-500">
                Due {toDisplayTime(item.due_time)}
              </span>
            </div>
          ))
        ) : (
          <p className="py-5 text-sm font-medium text-slate-500">
            All visible submissions are complete.
          </p>
        )}
      </InsightList>

      <InsightList
        title="Most active projects"
        count={projects.length}
        color="upcoming"
      >
        {projects.length ? (
          projects.map((project, index) => (
            <div key={project.id} className="flex items-center gap-3 py-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-violet-100 text-xs font-bold text-violet-700">
                {getInitials(project.name)}
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-bold text-slate-950">
                  {project.name}
                </p>
                <p className="truncate text-xs font-medium text-slate-500">
                  Rank {index + 1}
                </p>
              </div>
              <span className="text-xs font-semibold text-slate-500">
                {project.count} updates
              </span>
            </div>
          ))
        ) : (
          <p className="py-5 text-sm font-medium text-slate-500">
            No project activity yet.
          </p>
        )}
      </InsightList>
    </div>
  );
}

function ActionChip({ action, index = 0 }) {
  const palette = projectPalette[index % projectPalette.length];
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-[10px] font-medium ${palette}`}
    >
      <span className="h-1 w-1 rounded-full bg-current opacity-70" />
      {action.label}
    </span>
  );
}

function EmployeeCards({ employees, onOpen }) {
  return (
    <section className="mt-8">
      <div className="mb-3 flex items-end justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-950">
            Employee responsibilities
          </h2>
          <p className="text-sm font-medium text-slate-500">
            Ownership and completion at a glance
          </p>
        </div>
        <button type="button" className="text-sm font-bold text-violet-800">
          View all ›
        </button>
      </div>
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        {employees.map((employee, index) => (
          <button
            key={employee.id}
            type="button"
            onClick={() => onOpen(employee)}
            className={`rounded-xl flex flex-col items-start border bg-white px-4 py-3 text-left transition hover:-translate-y-0.5 hover:shadow-md ${index % 2 ? "border-violet-300 ring-1 ring-violet-200" : "border-slate-200"}`}
          >
            <div className="flex items-start w-full justify-between gap-4">
              <div className="flex min-w-0 items-center gap-4">
                {employee.userpic ? (
                  <img
                    src={employee.userpic}
                    alt={`${employee.name}'s profile`}
                    style={{ objectFit: "cover" }}
                    className="h-10 w-10 shrink-0 rounded-full"
                  />
                ) : (
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-violet-700 text-sm font-bold text-white">
                    {employee.initials}
                  </span>
                )}
                <div className="min-w-0">
                  <p className="truncate text-base font-semibold">
                    {employee.name}
                  </p>
                  <p className="truncate text-sm font-medium text-slate-500">
                    {employee.role}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xl font-semibold text-slate-950">
                  {employee.onTimePercent}%
                </p>
                <p className="text-xs font-medium text-slate-500">on time</p>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-3 gap-3">
              <div className="rounded-xl bg-slate-50 py-2 px-3 flex flex-col items-center">
                <p className="text-xs font-semibold uppercase text-slate-500">
                  Projects
                </p>
                <p className="mt-1 text-base font-semibold text-slate-950">
                  {employee.projectCount}
                </p>
              </div>
              <div className="rounded-xl bg-slate-50 p-2 flex flex-col items-center">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Active
                </p>
                <p className="mt-1 text-base font-semibold text-slate-950">
                  {employee.active}
                </p>
              </div>
              <div className="rounded-xl bg-slate-50 p-2 flex flex-col items-center">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Overdue
                </p>
                <p className="mt-1 text-base font-semibold text-red-500">
                  {employee.overdue}
                </p>
              </div>
            </div>

            <div className="mt-3 space-y-3">
              {employee.projects.length ? (
                employee.projects.slice(0, 2).map((project) => (
                  <div
                    key={project.id}
                    className="grid grid-cols-[160px_1fr] items-start gap-3"
                  >
                    <p className="truncate text-[13px] font-medium text-slate-500">
                      {project.name}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {project.actions
                        .slice(0, 3)
                        .map((action, actionIndex) => (
                          <ActionChip
                            key={`${project.id}-${action.value}`}
                            action={action}
                            index={actionIndex}
                          />
                        ))}
                      {project.actions.length > 3 && (
                        <span className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[10px] font-medium bg-slate-200 text-slate-600">
                          +{project.actions.length - 3} more
                        </span>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm font-medium text-slate-500">
                  No assigned reminders for the selected date.
                </p>
              )}
            </div>
          </button>
        ))}
      </div>
    </section>
  );
}

function EmployeeMatrix({ rows, employees }) {
  const projects = Array.from(
    rows
      .reduce((map, item) => {
        const key = String(item.site || "GLOBAL");
        if (!map.has(key))
          map.set(key, {
            id: key,
            name: item.projectName,
            code: getProjectCode(item.projectName, item.site || 1),
          });
        return map;
      }, new Map())
      .values(),
  );
  const matrixEmployees = employees
    .filter((employee) => employee.total > 0)
    .slice(0, 8);

  return (
    <section className="mt-6 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="p-4">
        <h2 className="text-lg font-semibold text-slate-950">
          Project × Employee matrix
        </h2>
        <p className="text-sm font-medium text-slate-500">
          Assigned action counts across teams and sites
        </p>
      </div>
      <div className="overflow-x-auto border-t border-slate-100">
        <div className="">
          <div
            className="grid bg-slate-50 text-sm items-center font-semibold text-slate-500"
            style={{
              gridTemplateColumns: `240px repeat(${Math.max(projects.length, 1)}, minmax(150px, 1fr))`,
            }}
          >
            <div className="pl-24 py-3">Employee</div>
            {projects.map((project) => (
              <div key={project.id} className="px-3 py-3 text-center">
                {/* <p className="font-bold text-slate-950">{project.code}</p> */}
                <p className="truncate text-xs">{project.name}</p>
              </div>
            ))}
          </div>
          {matrixEmployees.length ? (
            matrixEmployees.map((employee) => (
              <div
                key={employee.id}
                className="grid min-h-[76px] items-center border-t border-slate-100"
                style={{
                  gridTemplateColumns: `240px repeat(${Math.max(projects.length, 1)}, minmax(150px, 1fr))`,
                }}
              >
                <div className="flex items-center gap-3 px-3">
                  {employee.userpic ? (
                    <img
                      src={employee.userpic}
                      alt={`${employee.name}'s profile`}
                      className="h-10 w-10 shrink-0 rounded-full"
                      style={{ objectFit: "cover" }}
                    />
                  ) : (
                    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-violet-700 text-xs font-bold text-white">
                      {employee.initials}
                    </span>
                  )}
                  <div className="min-w-0">
                    <p className="truncate text-sm font-bold text-slate-950">
                      {employee.name}
                    </p>
                    <p className="truncate text-xs font-medium text-slate-500">
                      {employee.role}
                    </p>
                  </div>
                </div>
                {projects.map((project, index) => {
                  const count = employee.actions.filter(
                    (item) =>
                      String(item.site || "GLOBAL") === String(project.id),
                  ).length;
                  const palette = [
                    "bg-violet-100 text-violet-700",
                    "bg-amber-100 text-amber-700",
                    "bg-red-100 text-red-600",
                  ][index % 3];
                  return (
                    <div
                      key={`${employee.id}-${project.id}`}
                      className="px-3 text-center"
                    >
                      {count ? (
                        <span
                          className={`inline-flex justify-center rounded-xl px-4 py-2 text-sm font-bold ${palette}`}
                        >
                          {count}
                        </span>
                      ) : (
                        <span className="text-slate-300">·</span>
                      )}
                    </div>
                  );
                })}
              </div>
            ))
          ) : (
            <div className="flex h-48 items-center justify-center border-t border-slate-100 text-sm font-medium text-slate-500">
              No matrix data for this date.
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function EmployeeDetailDrawer({
  employee,
  members,
  reminders = [],
  selectedDate,
  onClose,
  onEdit,
  onCreate,
  onUpdateStatus,
  onCloseReminder,
}) {
  const [detailReminders, setDetailReminders] = useState([]);
  const [reminderToDelete, setReminderToDelete] = useState(null);
  const [deletingReminder, setDeletingReminder] = useState(false);
  const employeeId = employee?.id;

  const getEmployeeReminders = useCallback(
    (items = reminders) =>
      items
        .map(normalizeReminderAssignee)
        .filter(
          (item) =>
            String(item.assignee) === String(employeeId) &&
            item.status !== "CLOSED",
        )
        .sort((a, b) => {
          const aDate = getNextDueDateKey(a) || a.start_date || "";
          const bDate = getNextDueDateKey(b) || b.start_date || "";
          if (aDate !== bDate) return aDate.localeCompare(bDate);
          return String(a.due_time || "").localeCompare(
            String(b.due_time || ""),
          );
        }),
    [employeeId, reminders],
  );

  useEffect(() => {
    if (!employee) return;
    setDetailReminders(getEmployeeReminders());
  }, [employee, getEmployeeReminders]);

  const detailRows = useMemo(
    () =>
      detailReminders.map((reminder) => {
        const nextDueDateKey =
          getNextDueDateKey(reminder) || selectedDate || getLocalDateKey();
        const [row] = buildOperationalRows([reminder], members, nextDueDateKey);
        return {
          ...row,
          nextDueDateKey,
          nextDueLabel: getNextDueLabel(reminder),
        };
      }),
    [detailReminders, members, selectedDate],
  );

  const detailStats = useMemo(() => {
    return {
      assigned: detailRows.length,
      active: detailRows.filter((item) => item.status === "ACTIVE").length,
      overdue: detailRows.filter((item) => item.occurrenceStatus === "overdue")
        .length,
      paused: detailRows.filter((item) => item.status === "PAUSED").length,
    };
  }, [detailRows]);

  const handleStatusChange = async (item, status) => {
    const updatedReminder = { ...item, status };
    setDetailReminders((current) =>
      current.map((reminder) =>
        String(reminder.id) === String(item.id)
          ? normalizeReminderAssignee(updatedReminder)
          : reminder,
      ),
    );
    try {
      const updated = await onUpdateStatus(item, status);
      if (updated === false) {
        setDetailReminders(getEmployeeReminders());
      }
    } catch (error) {
      setDetailReminders(getEmployeeReminders());
    }
  };

  const handleConfirmDelete = async () => {
    if (!reminderToDelete) return;
    setDeletingReminder(true);
    const deleted = await onCloseReminder(reminderToDelete);
    setDeletingReminder(false);
    if (deleted === false) return;
    setDetailReminders((current) =>
      current.filter(
        (reminder) => String(reminder.id) !== String(reminderToDelete.id),
      ),
    );
    setReminderToDelete(null);
  };

  if (!employee) return null;
  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <button
        type="button"
        aria-label="Close employee details overlay"
        className="absolute inset-0 bg-slate-900/45 backdrop-blur-[2px]"
        onClick={onClose}
      />
      <aside className="relative flex h-full w-full max-w-[520px] flex-col bg-white shadow-xl">
        <header className="flex items-center justify-between border-b border-slate-200 px-6 py-3">
          <div className="flex min-w-0 items-center gap-4">
            {employee.userpic ? (
              <img
                src={employee.userpic}
                alt={`${employee.name}'s profile`}
                className="h-12 w-12 shrink-0 rounded-full"
                style={{ objectFit: "cover" }}
              />
            ) : (
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-violet-700 text-sm font-bold text-white">
                {employee.initials}
              </span>
            )}
            <div className="min-w-0">
              <h2 className="truncate text-lg font-bold text-slate-950">
                {employee.name}
              </h2>
              <p className="text-sm font-medium text-slate-500">
                {employee.role} · All assigned actions
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 text-slate-500 transition hover:border-sm-accent hover:text-sm-accent"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </header>
        <div className="grid grid-cols-3 gap-3 border-b border-slate-100 px-6 py-4">
          <div className="rounded-xl bg-slate-50 p-3">
            <p className="text-xs font-semibold uppercase text-slate-500">
              Assigned
            </p>
            <p className="mt-2 text-xl font-bold text-slate-950">
              {detailStats.assigned}
            </p>
          </div>
          <div className="rounded-xl bg-slate-50 p-3">
            <p className="text-xs font-semibold uppercase text-slate-500">
              Active
            </p>
            <p className="mt-2 text-xl font-bold text-emerald-600">
              {detailStats.active}
            </p>
          </div>
          <div className="rounded-xl bg-slate-50 p-3">
            <p className="text-xs font-semibold uppercase text-slate-500">
              Paused
            </p>
            <p className="mt-2 text-xl font-bold text-slate-700">
              {detailStats.paused}
            </p>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-5">
          <div className="mb-3 flex items-center justify-between gap-3">
            <h3 className="text-sm font-bold text-slate-950">
              Detailed reminders
            </h3>
            <button
              type="button"
              onClick={() => onCreate(employee)}
              className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-sm-accent px-3 text-xs font-bold text-white transition hover:bg-[#e56d00]"
            >
              <Plus size={14} />
              Create action
            </button>
          </div>
          <div className="space-y-3">
            {detailRows.length ? (
              detailRows.map((item) => (
                <div
                  key={`detail-${item.id}`}
                  className={`rounded-xl border p-4 ${
                    item.status === "PAUSED"
                      ? "border-slate-200 bg-slate-50"
                      : "border-slate-200 bg-white"
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-bold text-slate-950">
                        {formatActionLabel(item.action_type)}
                      </p>
                      <p className="mt-1 text-xs font-medium text-slate-500">
                        {item.projectName} · Next due {item.nextDueLabel}
                      </p>
                    </div>
                    <StatusBadge status={getDetailStatus(item)} />
                  </div>
                  {item.frequency === "CUSTOM_DAYS" && (
                    <WeekdayChips
                      days={getNormalizedCustomDays(item)}
                      className="mt-3"
                    />
                  )}
                  {item.notes && (
                    <p className="mt-3 text-sm font-medium text-slate-600">
                      {item.notes}
                    </p>
                  )}
                  {item.completion && (
                    <p className="mt-3 text-xs font-semibold text-slate-500">
                      Completed {formatDateToTime(item.completion.completed_at)}{" "}
                      · {item.completion.progress_completed || 0}/
                      {item.completion.progress_total || 0}
                    </p>
                  )}
                  <div className="mt-4 flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={() => onEdit(item)}
                      className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-slate-200 px-3 text-xs font-bold text-slate-700 transition hover:border-sm-accent hover:text-sm-accent"
                    >
                      <Edit3 size={14} />
                      Edit
                    </button>
                    {item.status === "PAUSED" ? (
                      <button
                        type="button"
                        onClick={() => handleStatusChange(item, "ACTIVE")}
                        className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-slate-200 px-3 text-xs font-bold text-slate-700 transition hover:border-emerald-300 hover:text-emerald-700"
                      >
                        <Play size={14} />
                        Resume
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleStatusChange(item, "PAUSED")}
                        disabled={item.status !== "ACTIVE"}
                        className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-slate-200 px-3 text-xs font-bold text-slate-700 transition hover:border-amber-300 hover:text-amber-700 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <Pause size={14} />
                        Pause
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => setReminderToDelete(item)}
                      disabled={item.status === "CLOSED"}
                      className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-red-100 px-3 text-xs font-bold text-red-600 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <Trash2 size={14} />
                      Delete
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <p className="rounded-xl border border-dashed border-slate-200 p-6 text-center text-sm font-medium text-slate-500">
                No reminders assigned to this employee.
              </p>
            )}
          </div>
        </div>
      </aside>
      <ConfirmationModal
        isOpen={Boolean(reminderToDelete)}
        onClose={() => {
          if (!deletingReminder) setReminderToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        title="Delete reminder?"
        message={`This will remove "${reminderToDelete?.title || formatActionLabel(reminderToDelete?.action_type)}" from active schedules. This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        isDeleting={deletingReminder}
      />
    </div>
  );
}

function StepIndicator({ step }) {
  const labels = ["Assignee & Project", "Action Items", "Configure", "Review"];
  return (
    <div className="flex items-center gap-2 overflow-x-auto">
      {labels.map((label, index) => {
        const item = index + 1;
        return (
          <React.Fragment key={item}>
            <div
              className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 text-sm font-bold ${
                item < step
                  ? "border-green-500 bg-green-500 text-white"
                  : item === step
                    ? "border-sm-accent bg-sm-accent text-white"
                    : "border-slate-200 bg-white text-slate-500"
              }`}
            >
              {item < step ? <Check size={18} /> : item}
            </div>
            <span
              className={`max-w-[120px] truncate text-xs font-bold ${
                item === step ? "text-slate-950" : "text-slate-500"
              }`}
            >
              {label}
            </span>
            {item < labels.length && (
              <div
                className={`h-0.5 w-10 rounded-full ${item < step ? "bg-green-500" : "bg-slate-200"}`}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function ActionConfigCard({
  actionType,
  config,
  expanded,
  onToggle,
  onUpdate,
}) {
  const action = getActionMeta(actionType);
  const Icon = action.icon;
  const timeParts = getTimeParts(config.due_time);
  const updateDueTimePart = (field, value) => {
    onUpdate(
      "due_time",
      toFormTime({
        ...timeParts,
        [field]: value,
      }),
    );
  };

  const toggleDay = (day) => {
    if (config.frequency === "WEEKLY") {
      onUpdate("custom_days", [day]);
      return;
    }

    onUpdate(
      "custom_days",
      config.custom_days.includes(day)
        ? config.custom_days.filter((item) => item !== day)
        : [...config.custom_days, day],
    );
  };

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center gap-4 px-4 py-4 text-left"
      >
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-sm-muted text-sm-accent">
          <Icon size={20} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-base font-bold text-slate-950">
            {action.label}
          </span>
          <span className="mt-1 flex flex-wrap items-center gap-2 text-xs font-semibold text-slate-500">
            {FREQUENCIES.find((item) => item.value === config.frequency)
              ?.label || config.frequency}
            <span>·</span>
            <Clock size={14} />
            {toDisplayTime(config.due_time)}
            <span className="inline-flex items-center gap-1 text-emerald-600">
              <span className="h-2 w-2 rounded-full bg-emerald-500" />
              Ready
            </span>
          </span>
        </span>
        <ChevronDown
          size={20}
          className={`text-slate-500 transition ${expanded ? "rotate-180" : ""}`}
        />
      </button>

      {expanded && (
        <div className="space-y-5 border-t border-slate-100 px-4 py-4">
          <div>
            <h4 className="mb-3 text-xs font-bold uppercase tracking-wide text-slate-600">
              Schedule & Frequency
            </h4>
            <div className="flex flex-wrap gap-2">
              {FREQUENCIES.map((item) => {
                const selected = config.frequency === item.value;
                return (
                  <button
                    key={item.value}
                    type="button"
                    onClick={() => onUpdate("frequency", item.value)}
                    className={`h-9 rounded-full border px-4 text-xs font-bold transition ${
                      selected
                        ? "border-sm-accent bg-sm-accent text-white"
                        : "border-slate-200 bg-white text-slate-700 hover:border-sm-accent/60"
                    }`}
                  >
                    {item.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            {!["CUSTOM_DATE", "ONCE"].includes(config.frequency) && (
              <div>
                <label className="mb-2 block text-xs font-bold uppercase text-slate-600">
                  Start date
                </label>
                <input
                  type="date"
                  value={config.start_date}
                  onChange={(event) =>
                    onUpdate("start_date", event.target.value)
                  }
                  className="h-10 w-full rounded-lg border border-slate-200 px-3 text-sm font-semibold text-slate-700 outline-none focus:border-sm-accent focus:ring-2 focus:ring-orange-100"
                />
              </div>
            )}
            {["CUSTOM_DATE", "ONCE"].includes(config.frequency) && (
              <div>
                <label className="mb-2 block text-xs font-bold uppercase text-slate-600">
                  Reminder date
                </label>
                <input
                  type="date"
                  value={config.custom_date}
                  onChange={(event) =>
                    onUpdate("custom_date", event.target.value)
                  }
                  className="h-10 w-full rounded-lg border border-slate-200 px-3 text-sm font-semibold text-slate-700 outline-none focus:border-sm-accent focus:ring-2 focus:ring-orange-100"
                />
              </div>
            )}
            <div>
              <label className="mb-2 block text-xs font-bold uppercase text-slate-600">
                Due time
              </label>
              <div className="grid grid-cols-[1fr_1fr_1.1fr] gap-2">
                <select
                  value={timeParts.hour}
                  onChange={(event) =>
                    updateDueTimePart("hour", event.target.value)
                  }
                  className="h-10 rounded-lg border border-slate-200 bg-white px-2 text-sm font-semibold text-slate-700 outline-none focus:border-sm-accent focus:ring-2 focus:ring-orange-100"
                >
                  {Array.from({ length: 12 }, (_, index) =>
                    String(index + 1).padStart(2, "0"),
                  ).map((hour) => (
                    <option key={hour} value={hour}>
                      {hour}
                    </option>
                  ))}
                </select>
                <select
                  value={timeParts.minute}
                  onChange={(event) =>
                    updateDueTimePart("minute", event.target.value)
                  }
                  className="h-10 rounded-lg border border-slate-200 bg-white px-2 text-sm font-semibold text-slate-700 outline-none focus:border-sm-accent focus:ring-2 focus:ring-orange-100"
                >
                  {Array.from({ length: 60 }, (_, index) =>
                    String(index).padStart(2, "0"),
                  ).map((minute) => (
                    <option key={minute} value={minute}>
                      {minute}
                    </option>
                  ))}
                </select>
                <select
                  value={timeParts.period}
                  onChange={(event) =>
                    updateDueTimePart("period", event.target.value)
                  }
                  className="h-10 rounded-lg border border-slate-200 bg-white px-2 text-sm font-semibold text-slate-700 outline-none focus:border-sm-accent focus:ring-2 focus:ring-orange-100"
                >
                  <option value="AM">AM</option>
                  <option value="PM">PM</option>
                </select>
              </div>
            </div>
          </div>

          {["WEEKLY", "CUSTOM_DAYS"].includes(config.frequency) && (
            <div>
              <h4 className="mb-3 text-xs font-bold uppercase text-slate-600">
                {config.frequency === "WEEKLY"
                  ? "Select weekday"
                  : "Select days"}
              </h4>
              <div className="flex flex-wrap gap-2">
                {WEEKDAYS.map((day) => {
                  const selected =
                    config.frequency === "WEEKLY"
                      ? getWeeklyDay(config) === day.value
                      : config.custom_days.includes(day.value);
                  return (
                    <button
                      key={day.value}
                      type="button"
                      onClick={() => toggleDay(day.value)}
                      className={`flex h-9 w-9 items-center justify-center rounded-full border text-sm font-bold transition ${
                        selected
                          ? "border-sm-accent bg-sm-accent text-white"
                          : "border-slate-200 bg-white text-slate-600 hover:border-sm-accent"
                      }`}
                    >
                      {day.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div>
            <label className="mb-2 block text-xs font-bold uppercase text-slate-600">
              Notes for assignee
            </label>
            <textarea
              value={config.notes}
              onChange={(event) => onUpdate("notes", event.target.value)}
              rows={3}
              className="w-full rounded-lg border border-slate-200 px-3 py-3 text-sm font-medium text-slate-700 outline-none focus:border-sm-accent focus:ring-2 focus:ring-orange-100"
              placeholder="Add completion instructions or project context."
            />
          </div>
        </div>
      )}
    </div>
  );
}

function ReminderDrawer({
  open,
  mode,
  step,
  form,
  setForm,
  members,
  sites,
  reminders,
  actionPermissionState,
  saving,
  onClose,
  onStep,
  onSubmit,
}) {
  const selectedSiteIds = getSelectedSiteIds(form);
  const selectedSites = sites.filter((site) =>
    selectedSiteIds.includes(String(site.id)),
  );
  const availableActionTypes = getAvailableActionTypes(form.scope);
  const selectedActionTypes = getSelectedActionTypes(form);
  const selectedMember = members.find(
    (member) => String(member.id) === String(form.assignee),
  );
  const assignableMembers = useMemo(
    () => members.filter((member) => !isOwnerMember(member)),
    [members],
  );
  const [projectSearch, setProjectSearch] = useState("");
  const [expandedAction, setExpandedAction] = useState("");
  const filteredSites = sites.filter((site) =>
    site.displayName
      ?.toLowerCase()
      .includes(projectSearch.trim().toLowerCase()),
  );

  useEffect(() => {
    if (!open) {
      setProjectSearch("");
      setExpandedAction("");
    }
  }, [open]);

  useEffect(() => {
    if (!expandedAction && selectedActionTypes.length) {
      setExpandedAction(selectedActionTypes[0]);
    }
  }, [expandedAction, selectedActionTypes]);

  if (!open) return null;

  const toggleSite = (siteId) => {
    setForm((prev) => {
      const id = String(siteId);
      const current = getSelectedSiteIds(prev);
      const nextIds = current.includes(id) ? [] : [id];
      return {
        ...prev,
        site: nextIds[0] || "",
        sites: nextIds,
        selected_actions: mode === "edit" ? prev.selected_actions : [],
        action_configs: mode === "edit" ? prev.action_configs : {},
        reassignments: mode === "edit" ? prev.reassignments : {},
      };
    });
  };

  const selectMember = (memberId) => {
    setForm((prev) => ({
      ...prev,
      assignee: String(memberId),
      selected_actions: mode === "edit" ? prev.selected_actions : [],
      action_configs: mode === "edit" ? prev.action_configs : {},
      reassignments: mode === "edit" ? prev.reassignments : {},
    }));
  };

  const updateActionConfig = (actionType, field, value) => {
    setForm((prev) => {
      const current = getActionConfig(prev, actionType);
      const nextConfig = { ...current, [field]: value };
      if (field === "frequency" && value !== "CUSTOM_DAYS") {
        nextConfig.custom_days = [];
      }
      if (field === "frequency" && value === "WEEKLY") {
        nextConfig.custom_days = [getWeeklyDay(nextConfig)];
      }
      if (
        field === "frequency" &&
        value === "ONCE" &&
        !nextConfig.custom_date
      ) {
        nextConfig.custom_date = nextConfig.start_date || getLocalDateKey();
      }
      if (field === "frequency" && !["CUSTOM_DATE", "ONCE"].includes(value)) {
        nextConfig.custom_date = "";
      }
      return {
        ...prev,
        action_configs: {
          ...prev.action_configs,
          [actionType]: nextConfig,
        },
      };
    });
  };

  const toggleAction = (actionType, existingReminder) => {
    const permissionStatus = getActionPermissionStatus(
      actionType,
      form,
      actionPermissionState,
    );
    if (!permissionStatus.allowed) return;

    if (
      existingReminder &&
      String(existingReminder.assignee) === String(form.assignee)
    ) {
      return;
    }

    setForm((prev) => {
      const selected = getSelectedActionTypes(prev).includes(actionType);
      const nextSelected =
        mode === "edit"
          ? [actionType]
          : selected
            ? getSelectedActionTypes(prev).filter((item) => item !== actionType)
            : [...getSelectedActionTypes(prev), actionType];
      const nextConfigs = { ...(prev.action_configs || {}) };
      const nextReassignments = { ...(prev.reassignments || {}) };

      if (selected) {
        delete nextConfigs[actionType];
        delete nextReassignments[actionType];
      } else {
        nextConfigs[actionType] =
          existingReminder &&
          String(existingReminder.assignee) !== String(prev.assignee)
            ? getDefaultActionConfig(existingReminder)
            : getDefaultActionConfig();
        if (
          existingReminder &&
          String(existingReminder.assignee) !== String(prev.assignee)
        ) {
          nextReassignments[actionType] = existingReminder.id;
        }
      }

      return {
        ...prev,
        action_type: actionType,
        selected_actions: nextSelected,
        action_configs: nextConfigs,
        reassignments: nextReassignments,
      };
    });
  };

  const applyFirstConfigToAll = () => {
    if (selectedActionTypes.length < 2) return;
    const firstConfig = getActionConfig(form, selectedActionTypes[0]);
    setForm((prev) => ({
      ...prev,
      action_configs: selectedActionTypes.reduce(
        (configs, actionType) => ({
          ...configs,
          [actionType]: { ...firstConfig },
        }),
        {},
      ),
    }));
  };

  const resetConfigs = () => {
    setForm((prev) => ({
      ...prev,
      action_configs: selectedActionTypes.reduce(
        (configs, actionType) => ({
          ...configs,
          [actionType]: getDefaultActionConfig(),
        }),
        {},
      ),
    }));
  };

  const selectedPlaceLabel =
    form.scope === "GLOBAL"
      ? "Organization"
      : selectedSites[0]?.displayName || "Project";
  const selectedMemberMissingFromSite =
    form.scope === "PROJECT" &&
    actionPermissionState?.loaded &&
    !actionPermissionState?.loading &&
    !actionPermissionState?.siteMember;

  if (mode === "edit") {
    const actionType = selectedActionTypes[0] || form.action_type;

    return (
      <div className="fixed inset-0 z-50 flex justify-end">
        <button
          type="button"
          aria-label="Close drawer overlay"
          className="absolute inset-0 bg-slate-900/45 backdrop-blur-[2px]"
          onClick={onClose}
        />
        <aside className="relative flex h-full w-full max-w-[620px] flex-col bg-white shadow-xl">
          <header className="border-b border-slate-200 px-8 py-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-xl font-bold text-slate-950">
                  Edit Action Schedule
                </h2>
                <p className="mt-1 text-sm font-medium text-slate-500">
                  {formatActionLabel(actionType)} · {selectedPlaceLabel}
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 text-slate-500 transition hover:border-sm-accent hover:text-sm-accent"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>
          </header>

          <div className="flex-1 overflow-y-auto px-8 py-6">
            <ActionConfigCard
              actionType={actionType}
              config={getActionConfig(form, actionType)}
              expanded
              onToggle={() => {}}
              onUpdate={(field, value) =>
                updateActionConfig(actionType, field, value)
              }
            />
          </div>

          <footer className="flex items-center justify-end gap-3 border-t border-slate-200 px-8 py-4 text-sm">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="h-10 rounded-lg border border-slate-200 px-6 font-bold text-slate-800 transition hover:border-slate-300 hover:bg-slate-50 disabled:opacity-60"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onSubmit}
              disabled={saving}
              className="inline-flex h-10 items-center gap-2 rounded-lg bg-sm-accent px-6 font-bold text-white transition hover:bg-[#e56d00] disabled:cursor-not-allowed disabled:opacity-70"
            >
              {saving && <Loader2 size={18} className="animate-spin" />}
              Save changes
            </button>
          </footer>
        </aside>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <button
        type="button"
        aria-label="Close drawer overlay"
        className="absolute inset-0 bg-slate-900/45 backdrop-blur-[2px]"
        onClick={onClose}
      />
      <aside className="relative flex h-full w-full max-w-[720px] flex-col bg-white shadow-xl">
        <header className="border-b border-slate-200 px-8 py-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-xl font-bold text-slate-950">
                {mode === "edit"
                  ? "Edit Action Assignment"
                  : "New Action Assignment"}
              </h2>
              <p className="text-sm font-medium text-slate-500">
                Assign recurring operational responsibilities across projects.
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 text-slate-500 transition hover:border-sm-accent hover:text-sm-accent"
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </div>
          <div className="mt-6">
            <StepIndicator step={step} />
          </div>
        </header>

        <div className="flex-1 overflow-y-auto px-8 py-6">
          {step === 1 && (
            <div className="space-y-7">
              <section>
                <h3 className="text-xs font-bold uppercase tracking-wide text-slate-600">
                  Assignee
                </h3>
                <p className="mt-1 text-sm font-medium text-slate-500">
                  Select the employee responsible for these actions.
                </p>
                <div className="relative mt-4">
                  <UserCheck
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                    size={18}
                  />
                  <select
                    value={form.assignee}
                    onChange={(event) => selectMember(event.target.value)}
                    className="h-12 w-full appearance-none rounded-xl border border-slate-200 bg-white pl-12 pr-10 text-sm font-semibold text-slate-900 outline-none transition focus:border-sm-accent focus:ring-2 focus:ring-orange-100"
                  >
                    <option value="">Select employee</option>
                    {assignableMembers.map((member) => (
                      <option key={member.id} value={member.id}>
                        {member.displayName}
                      </option>
                    ))}
                  </select>
                  <ChevronDown
                    size={18}
                    className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-slate-400"
                  />
                </div>

                {/* {selectedMember && (
                  <div className="mt-3 flex items-center gap-4 rounded-xl border-2 border-sm-accent/40 bg-orange-50/50 p-3">
                    <div
                      className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl text-base font-semibold ${selectedMember.accentClass}`}
                    >
                      {selectedMember.initials}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-base font-bold text-slate-950">
                        {selectedMember.displayName}
                      </p>
                      <p className="text-xs font-medium text-slate-500">
                        {getMemberRole(selectedMember)}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => selectMember("")}
                      className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 transition hover:bg-white hover:text-red-500"
                      aria-label="Clear assignee"
                    >
                      <X size={18} />
                    </button>
                  </div>
                )} */}
              </section>

              <section>
                <h3 className="text-xs font-bold uppercase tracking-wide text-slate-600">
                  Project
                </h3>
                <p className="mt-1 text-sm font-medium text-slate-500">
                  Select the project where these responsibilities apply.
                </p>
                <div className="mt-4">
                  <div className="relative">
                    <Search
                      className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                      size={18}
                    />
                    <input
                      value={projectSearch}
                      onChange={(event) => setProjectSearch(event.target.value)}
                      className="h-12 w-full rounded-xl border border-slate-200 pl-12 pr-4 text-sm font-medium text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-sm-accent focus:ring-2 focus:ring-orange-100"
                      placeholder="Search projects..."
                    />
                  </div>
                  {selectedSites.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {selectedSites.map((site) => (
                        <span
                          key={site.id}
                          className="inline-flex max-w-full items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700"
                        >
                          <span className="truncate">{site.displayName}</span>
                          <button
                            type="button"
                            onClick={() => toggleSite(site.id)}
                            className="text-slate-400 transition hover:text-red-500"
                            aria-label={`Remove ${site.displayName}`}
                          >
                            <X size={13} />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="mt-3 max-h-72 overflow-y-auto rounded-xl border border-slate-200">
                    {filteredSites.length === 0 ? (
                      <div className="px-4 py-8 text-center text-sm font-semibold text-slate-500">
                        No projects found
                      </div>
                    ) : (
                      filteredSites.map((site) => {
                        const selected = selectedSiteIds.includes(
                          String(site.id),
                        );
                        return (
                          <button
                            key={site.id}
                            type="button"
                            onClick={() => toggleSite(site.id)}
                            className={`flex w-full items-center gap-3 border-b border-slate-100 px-4 py-3 text-left transition last:border-b-0 ${
                              selected
                                ? "bg-orange-50"
                                : "bg-white hover:bg-slate-50"
                            }`}
                          >
                            <span
                              className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border ${
                                selected
                                  ? "border-sm-accent bg-sm-accent text-white"
                                  : "border-slate-300 bg-white"
                              }`}
                            >
                              {selected && <Check size={14} />}
                            </span>
                            <span className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-900">
                              {site.displayName}
                            </span>
                          </button>
                        );
                      })
                    )}
                  </div>
                </div>
              </section>
            </div>
          )}

          {step === 2 && (
            <div>
              <div className="mb-3 flex items-start justify-between gap-4">
                <div>
                  <h3 className="text-lg font-bold text-slate-950">
                    Select responsibilities
                  </h3>
                  <p className="text-xs font-medium text-slate-500">
                    Choose operational actions for{" "}
                    {selectedMember?.displayName || "the selected member"}.
                  </p>
                </div>
                <span className="rounded-full border border-sm-accent/30 bg-orange-50 px-3 py-1.5 text-xs font-semibold text-sm-accent">
                  {selectedActionTypes.length} selected
                </span>
              </div>

              <div className="mb-4 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                <p className="text-sm font-bold text-slate-950">
                  {selectedPlaceLabel}
                </p>
                <p className="text-xs font-medium text-slate-500">
                  Already assigned actions for this member in this site are
                  blocked. Actions owned by another member can be reassigned.
                </p>
              </div>

              {selectedMemberMissingFromSite ? (
                <div className="rounded-xl border border-red-100 bg-red-50 px-4 py-5">
                  <div className="flex items-start gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white text-red-500">
                      <AlertTriangle size={20} />
                    </span>
                    <div>
                      <h4 className="text-sm font-bold text-red-700">
                        Member not assigned to selected site
                      </h4>
                      <p className="mt-1 text-sm font-medium text-red-600">
                        {selectedMember?.displayName || "This member"} is not
                        part of {selectedPlaceLabel}, so action items cannot be
                        created for this site.
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="grid gap-3 sm:grid-cols-2">
                  {availableActionTypes.map((item) => {
                    const Icon = item.icon;
                    const existingReminder = findExistingActionReminder(
                      reminders,
                      form,
                      item.value,
                    );
                    const existingMember = existingReminder
                      ? members.find(
                          (member) =>
                            String(member.id) ===
                            String(existingReminder.assignee),
                        )
                      : null;
                    const alreadyAssignedToSelected =
                      existingReminder &&
                      String(existingReminder.assignee) ===
                        String(form.assignee);
                    const permissionStatus = getActionPermissionStatus(
                      item.value,
                      form,
                      actionPermissionState,
                    );
                    const blockedByPermission = !permissionStatus.allowed;
                    const disabled =
                      alreadyAssignedToSelected || blockedByPermission;
                    const selected = selectedActionTypes.includes(item.value);
                    return (
                      <button
                        key={item.value}
                        type="button"
                        onClick={() =>
                          toggleAction(item.value, existingReminder)
                        }
                        disabled={disabled}
                        className={` rounded-xl border-2 p-3 text-left transition disabled:cursor-not-allowed ${
                          disabled
                            ? "border-slate-200 bg-slate-50 opacity-70"
                            : selected
                              ? "border-sm-accent bg-orange-50 shadow-[0_0_0_3px_rgba(255,124,1,0.14)]"
                              : "border-slate-200 bg-white hover:border-sm-accent/70"
                        }`}
                      >
                        <span className="flex items-start gap-4">
                          <span
                            className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${
                              selected
                                ? "bg-sm-accent text-white"
                                : "bg-sm-muted text-slate-600"
                            }`}
                          >
                            <Icon size={20} />
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="flex items-center gap-2">
                              <span className="truncate text-base font-bold text-slate-950">
                                {item.label}
                              </span>
                              {selected && <Check size={17} />}
                            </span>
                            <span className="block text-sm font-medium text-slate-500">
                              {item.description}
                            </span>
                            {alreadyAssignedToSelected && (
                              <span className="mt-3 block text-xs font-bold text-slate-500">
                                Already assigned to this member
                              </span>
                            )}
                            {!alreadyAssignedToSelected &&
                              blockedByPermission && (
                                <span className="mt-3 block text-xs font-bold text-red-500">
                                  {permissionStatus.reason}
                                </span>
                              )}
                            {existingReminder && !alreadyAssignedToSelected && (
                              <span className="mt-3 flex flex-wrap items-center gap-2 text-xs font-semibold text-slate-500">
                                Managed by{" "}
                                <strong className="text-slate-800">
                                  {existingMember?.displayName ||
                                    getMemberName(existingReminder, members)}
                                </strong>
                                <span
                                  className={`inline-flex items-center gap-1 rounded-full border px-2 py-1 ${
                                    selected
                                      ? "border-sm-accent bg-white text-sm-accent"
                                      : "border-slate-200 bg-white text-slate-700"
                                  }`}
                                >
                                  <span
                                    className={`h-3 w-3 rounded border ${
                                      selected
                                        ? "border-sm-accent bg-sm-accent"
                                        : "border-slate-300"
                                    }`}
                                  />
                                  Reassign
                                </span>
                              </span>
                            )}
                          </span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {step === 3 && (
            <div className="space-y-5">
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                <div className="text-sm font-bold text-slate-950">
                  {selectedActionTypes.length} action
                  {selectedActionTypes.length === 1 ? "" : "s"} selected
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={applyFirstConfigToAll}
                    disabled={selectedActionTypes.length < 2}
                    className="inline-flex h-9 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-xs font-bold text-slate-700 transition hover:border-sm-accent hover:text-sm-accent disabled:opacity-40"
                  >
                    <ClipboardCheck size={15} />
                    Apply same to all
                  </button>
                  <button
                    type="button"
                    onClick={resetConfigs}
                    className="inline-flex h-9 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-xs font-bold text-slate-700 transition hover:border-slate-300"
                  >
                    <RefreshCcw size={15} />
                    Reset
                  </button>
                </div>
              </div>

              {selectedActionTypes.map((actionType) => (
                <ActionConfigCard
                  key={actionType}
                  actionType={actionType}
                  config={getActionConfig(form, actionType)}
                  expanded={expandedAction === actionType}
                  onToggle={() =>
                    setExpandedAction((current) =>
                      current === actionType ? "" : actionType,
                    )
                  }
                  onUpdate={(field, value) =>
                    updateActionConfig(actionType, field, value)
                  }
                />
              ))}
            </div>
          )}

          {step === 4 && (
            <div className="space-y-4">
              <section className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
                <h3 className="text-xs font-bold uppercase tracking-wide text-slate-600">
                  Employee
                </h3>
                <div className="mt-3 flex items-center gap-4">
                  <div
                    className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl text-base font-bold ${
                      selectedMember?.accentClass ||
                      "bg-slate-100 text-slate-600"
                    }`}
                  >
                    {selectedMember?.initials || "TM"}
                  </div>
                  <div>
                    <p className="text-base font-bold text-slate-950">
                      {selectedMember?.displayName || "Team member"}
                    </p>
                    <p className="text-sm font-medium text-slate-500">
                      {selectedMember ? getMemberRole(selectedMember) : ""}
                    </p>
                  </div>
                </div>
              </section>

              <section className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
                <div className="flex items-center justify-between gap-3">
                  <h3 className="text-xs font-bold uppercase tracking-wide text-slate-600">
                    Project included
                  </h3>
                  <span className="text-sm font-bold text-slate-500">
                    {selectedPlaceLabel}
                  </span>
                </div>
              </section>

              <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                <div className="border-b border-slate-100 px-4 py-3">
                  <h3 className="text-xs font-bold uppercase tracking-wide text-slate-600">
                    Action responsibilities
                  </h3>
                </div>
                <div className="grid grid-cols-[1.4fr_0.8fr_0.7fr_0.7fr] border-b border-slate-100 bg-slate-50 px-4 py-3 text-xs font-bold uppercase tracking-wide text-slate-500">
                  <span>Action</span>
                  <span>Frequency</span>
                  <span>Time</span>
                  <span>Status</span>
                </div>
                {selectedActionTypes.map((actionType) => {
                  const config = getActionConfig(form, actionType);
                  const reassigned = form.reassignments?.[actionType];
                  return (
                    <div
                      key={actionType}
                      className="grid grid-cols-[1.4fr_0.8fr_0.7fr_0.7fr] items-center border-b border-slate-100 px-4 py-3 text-sm last:border-b-0"
                    >
                      <span className="font-bold text-slate-950">
                        {formatActionLabel(actionType)}
                      </span>
                      <span className="font-medium text-slate-500">
                        {FREQUENCIES.find(
                          (item) => item.value === config.frequency,
                        )?.label || config.frequency}
                      </span>
                      <span className="font-medium text-slate-500">
                        {toDisplayTime(config.due_time)}
                      </span>
                      <span className="inline-flex w-fit items-center gap-1 rounded-full bg-emerald-50 px-2 py-1 text-xs font-bold text-emerald-700">
                        <span className="h-2 w-2 rounded-full bg-emerald-500" />
                        {reassigned ? "Reassign" : "Ready"}
                      </span>
                    </div>
                  );
                })}
              </section>
            </div>
          )}
        </div>

        <footer className="flex items-center justify-between border-t border-slate-200 px-8 py-4 text-sm">
          <button
            type="button"
            onClick={() => onStep(Math.max(1, step - 1))}
            disabled={step === 1 || saving}
            className="inline-flex h-10 items-center gap-2 rounded-lg border border-slate-200 px-5 font-bold text-slate-700 transition hover:border-sm-accent hover:text-sm-accent disabled:cursor-not-allowed disabled:opacity-40"
          >
            <ArrowLeft size={18} />
            Back
          </button>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="h-10 rounded-lg border border-slate-200 px-6 font-bold text-slate-800 transition hover:border-slate-300 hover:bg-slate-50 disabled:opacity-60"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={step === 4 ? onSubmit : () => onStep(step + 1)}
              disabled={saving}
              className="inline-flex h-10 items-center gap-2 rounded-lg bg-sm-accent px-6 font-bold text-white transition hover:bg-[#e56d00] disabled:cursor-not-allowed disabled:opacity-70"
            >
              {saving && <Loader2 size={18} className="animate-spin" />}
              {step === 4
                ? mode === "edit"
                  ? "Save changes"
                  : "Create actions"
                : "Continue"}
            </button>
          </div>
        </footer>
      </aside>
    </div>
  );
}

export default function Manage({
  organizationId: organizationIdProp,
  currentMember,
  isOwner,
}) {
  const { addAlert } = useAlert();
  const organizationId = getOrganizationId(organizationIdProp);
  const canManage =
    isOwner ||
    currentMember?.is_admin === true ||
    (currentMember?.joined_as || "").toLowerCase() === "owner" ||
    localStorage.getItem("isOwner") === "true";

  const [reminders, setReminders] = useState([]);
  const [members, setMembers] = useState([]);
  const [sites, setSites] = useState([]);
  const [todayItems, setTodayItems] = useState([]);
  const [dateReminders, setDateReminders] = useState([]);
  const [dateRemindersLoading, setDateRemindersLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [assigneeFilter, setAssigneeFilter] = useState("ALL");
  const [typeFilter, setTypeFilter] = useState("ALL");
  const [specificDateFilter, setSpecificDateFilter] =
    useState(getLocalDateKey());
  const [projectFilter, setProjectFilter] = useState("ALL");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerStep, setDrawerStep] = useState(1);
  const [drawerMode, setDrawerMode] = useState("create");
  const [editingReminder, setEditingReminder] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [actionPermissionState, setActionPermissionState] = useState(
    EMPTY_ACTION_PERMISSION_STATE,
  );

  const fetchReminders = useCallback(async () => {
    if (!organizationId) return;
    const response = await fetch(
      `${BASE_URL}/reminders/org/${organizationId}/`,
      {
        method: "GET",
        headers: getAuthHeaders(),
      },
    );
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data?.status === false) {
      throw new Error(data?.message || "Failed to fetch reminders");
    }
    const list = normalizeList(data);
    setReminders(list);
    return list;
  }, [organizationId]);

  const fetchMembers = useCallback(async () => {
    if (!organizationId) return;
    const response = await fetch(
      `${BASE_URL}/org/organization/${organizationId}/members/`,
      {
        method: "GET",
        headers: getAuthHeaders(),
      },
    );
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data?.status === false) {
      throw new Error(data?.message || "Failed to fetch organization members");
    }
    const list = normalizeList(data).map(normalizeMember);
    setMembers(list);
    return list;
  }, [organizationId]);

  const fetchSites = useCallback(async () => {
    if (!organizationId) return;
    const data = await ProjectApi.listSitesByOrganization(organizationId);
    const list = normalizeList(data).map(normalizeSite);
    setSites(list);
    return list;
  }, [organizationId]);

  const fetchTodayItems = useCallback(
    async (memberList) => {
      if (!organizationId || !memberList.length) {
        setTodayItems([]);
        return;
      }
      const settled = await Promise.allSettled(
        memberList.map((member) =>
          fetch(
            `${BASE_URL}/reminders/org/${organizationId}/assignee/${member.id}/today/`,
            {
              method: "GET",
              headers: getAuthHeaders(),
            },
          )
            .then((response) => response.json())
            .then((data) => normalizeList(data)),
        ),
      );
      setTodayItems(
        settled.flatMap((result) =>
          result.status === "fulfilled" ? result.value : [],
        ),
      );
    },
    [organizationId],
  );

  const fetchDateReminders = useCallback(
    async (dateKey = specificDateFilter) => {
      if (!organizationId || !dateKey) {
        setDateReminders([]);
        return [];
      }
      setDateRemindersLoading(true);
      try {
        const response = await fetch(
          `${BASE_URL}/reminders/org/${organizationId}/get-reminders/?date=${dateKey}`,
          {
            method: "GET",
            headers: getAuthHeaders(),
          },
        );
        const data = await response.json().catch(() => ({}));
        if (!response.ok || data?.status === false) {
          throw new Error(
            data?.detail ||
              data?.message ||
              "Failed to fetch reminders for the selected date",
          );
        }
        const list = normalizeList(data);
        setDateReminders(list);
        return list;
      } catch (error) {
        setDateReminders([]);
        addAlert(
          error?.message || "Failed to fetch reminders for the selected date",
          "error",
        );
        return [];
      } finally {
        setDateRemindersLoading(false);
      }
    },
    [addAlert, organizationId, specificDateFilter],
  );

  const loadData = useCallback(async () => {
    if (!organizationId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [membersResponse] = await Promise.all([
        fetchMembers(),
        fetchReminders(),
        fetchSites(),
        fetchDateReminders(specificDateFilter),
      ]);
      await fetchTodayItems(membersResponse || []);
    } catch (error) {
      addAlert(error?.message || "Failed to load action items", "error");
    } finally {
      setLoading(false);
    }
  }, [
    addAlert,
    fetchDateReminders,
    fetchMembers,
    fetchReminders,
    fetchSites,
    fetchTodayItems,
    organizationId,
    specificDateFilter,
  ]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!drawerOpen && !selectedEmployee) return;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [drawerOpen, selectedEmployee]);

  const selectedPermissionSiteId =
    drawerOpen && form.scope === "PROJECT"
      ? getSelectedSiteIds(form)[0] || ""
      : "";

  useEffect(() => {
    if (
      !drawerOpen ||
      drawerMode === "edit" ||
      form.scope !== "PROJECT" ||
      !form.assignee ||
      !selectedPermissionSiteId
    ) {
      setActionPermissionState(EMPTY_ACTION_PERMISSION_STATE);
      return;
    }

    let ignore = false;
    const loadActionPermissions = async () => {
      setActionPermissionState({
        ...EMPTY_ACTION_PERMISSION_STATE,
        loading: true,
      });
      try {
        const [siteMembersResponse, assignedOverviewResponse] =
          await Promise.all([
            fetch(
              `${BASE_URL}/team/list-site-members/${selectedPermissionSiteId}/`,
              {
                method: "GET",
                headers: getAuthHeaders(),
              },
            ),
            fetch(
              `${BASE_URL}/task/site/${selectedPermissionSiteId}/get-member-assigned-overview/${form.assignee}/`,
              {
                method: "GET",
                headers: getAuthHeaders(),
              },
            ),
          ]);

        const [siteMembersData, assignedOverviewData] = await Promise.all([
          siteMembersResponse.json().catch(() => ({})),
          assignedOverviewResponse.json().catch(() => ({})),
        ]);

        if (
          !siteMembersResponse.ok ||
          siteMembersData?.status === false ||
          !assignedOverviewResponse.ok ||
          assignedOverviewData?.status === false
        ) {
          throw new Error("Could not validate member permissions");
        }

        if (ignore) return;
        const siteMember = normalizeList(siteMembersData).find(
          (member) =>
            String(member?.organization_member_id) === String(form.assignee),
        );
        setActionPermissionState({
          loaded: true,
          loading: false,
          error: "",
          siteMember: siteMember || null,
          assignedOverview: normalizeAssignedOverview(assignedOverviewData),
        });
      } catch (error) {
        if (ignore) return;
        setActionPermissionState({
          ...EMPTY_ACTION_PERMISSION_STATE,
          loaded: true,
          error: error?.message || "Could not validate member permissions",
        });
      }
    };

    loadActionPermissions();

    return () => {
      ignore = true;
    };
  }, [
    drawerMode,
    drawerOpen,
    form.assignee,
    form.scope,
    selectedPermissionSiteId,
  ]);

  const stats = useMemo(() => {
    const active = reminders.filter((item) => item.status === "ACTIVE").length;
    const dueToday =
      dateReminders.length || reminders.filter(isReminderDueToday).length;
    const overdue = dateReminders.filter(
      (item) => getOccurrenceStatus(item, specificDateFilter) === "overdue",
    ).length;
    const paused = reminders.filter((item) => item.status === "PAUSED").length;
    const completedToday =
      dateReminders.filter((item) =>
        getCompletionForDate(item, specificDateFilter),
      ).length || todayItems.filter((item) => item.is_completed).length;
    return { active, dueToday, overdue, paused, completedToday };
  }, [dateReminders, reminders, specificDateFilter, todayItems]);

  const operationalRows = useMemo(
    () => buildOperationalRows(dateReminders, members, specificDateFilter),
    [dateReminders, members, specificDateFilter],
  );

  const projectOptions = useMemo(() => {
    const options = Array.from(
      operationalRows
        .reduce((map, item) => {
          const key = String(item.site || "GLOBAL");
          if (!map.has(key))
            map.set(key, { value: key, label: item.projectName });
          return map;
        }, new Map())
        .values(),
    );
    return options.sort((a, b) => a.label.localeCompare(b.label));
  }, [operationalRows]);

  const filteredOperationalRows = useMemo(
    () =>
      filterOperationalRows(operationalRows, {
        projectFilter,
        assigneeFilter,
        typeFilter,
        statusFilter,
      }),
    [assigneeFilter, operationalRows, projectFilter, statusFilter, typeFilter],
  );

  const employeeOverview = useMemo(
    () => buildEmployeeOverview(filteredOperationalRows, members),
    [filteredOperationalRows, members],
  );

  const validateStep = (nextStep) => {
    if (nextStep <= drawerStep) return true;
    if (drawerStep === 1) {
      if (!form.assignee) {
        addAlert("Please select an assignee", "error");
        return false;
      }
      if (
        !members.some(
          (member) =>
            !isOwnerMember(member) &&
            String(member.id) === String(form.assignee),
        )
      ) {
        addAlert("Please select a non-owner assignee", "error");
        return false;
      }
      if (!form.scope) {
        addAlert("Please choose a scope", "error");
        return false;
      }
      if (form.scope === "PROJECT" && getSelectedSiteIds(form).length !== 1) {
        addAlert("Please select one project", "error");
        return false;
      }
    }
    if (drawerStep === 2) {
      const selectedActions = getSelectedActionTypes(form);
      if (selectedActions.length === 0) {
        addAlert("Please choose at least one action item", "error");
        return false;
      }
      const availableValues = getAvailableActionTypes(form.scope).map(
        (item) => item.value,
      );
      if (
        !selectedActions.every((actionType) =>
          availableValues.includes(actionType),
        )
      ) {
        addAlert(
          "Please choose action items available for this scope",
          "error",
        );
        return false;
      }
      const blockedByPermission = selectedActions.find(
        (actionType) =>
          !getActionPermissionStatus(actionType, form, actionPermissionState)
            .allowed,
      );
      if (blockedByPermission) {
        const permissionStatus = getActionPermissionStatus(
          blockedByPermission,
          form,
          actionPermissionState,
        );
        addAlert(
          `${formatActionLabel(blockedByPermission)} cannot be assigned. ${permissionStatus.reason}`,
          "error",
        );
        return false;
      }
      const blockedAction = selectedActions.find((actionType) => {
        const existingReminder = findExistingActionReminder(
          reminders,
          form,
          actionType,
        );
        return (
          existingReminder &&
          String(existingReminder.assignee) === String(form.assignee)
        );
      });
      if (blockedAction) {
        addAlert(
          `${formatActionLabel(blockedAction)} is already assigned to this member for this site`,
          "error",
        );
        return false;
      }
    }
    if (drawerStep === 3) {
      for (const actionType of getSelectedActionTypes(form)) {
        const config = getActionConfig(form, actionType);
        if (
          (config.frequency === "ONCE" || config.frequency === "CUSTOM_DATE") &&
          !config.custom_date
        ) {
          addAlert(
            `Please select a reminder date for ${formatActionLabel(actionType)}`,
            "error",
          );
          return false;
        }
        if (
          !["ONCE", "CUSTOM_DATE"].includes(config.frequency) &&
          !config.start_date
        ) {
          addAlert(
            `Please select a start date for ${formatActionLabel(actionType)}`,
            "error",
          );
          return false;
        }
        if (
          config.frequency === "CUSTOM_DAYS" &&
          config.custom_days.length === 0
        ) {
          addAlert(
            `Please select custom days for ${formatActionLabel(actionType)}`,
            "error",
          );
          return false;
        }
        if (
          config.frequency === "WEEKLY" &&
          getWeeklyDay(config).length === 0
        ) {
          addAlert(
            `Please select a weekday for ${formatActionLabel(actionType)}`,
            "error",
          );
          return false;
        }
        if (!config.due_time) {
          addAlert(
            `Please select a due time for ${formatActionLabel(actionType)}`,
            "error",
          );
          return false;
        }
      }
    }
    return true;
  };

  const handleStep = (nextStep) => {
    if (!validateStep(nextStep)) return;
    setDrawerStep(nextStep);
  };

  const openCreateDrawer = (employee = null) => {
    setDrawerMode("create");
    setEditingReminder(null);
    setForm({
      ...EMPTY_FORM,
      action_type: getDefaultActionType("PROJECT"),
      selected_actions: [],
      action_configs: {},
      reassignments: {},
      assignee: employee?.id ? String(employee.id) : "",
      scope: "PROJECT",
      site: "",
      sites: [],
      custom_days: [],
    });
    setDrawerStep(1);
    setDrawerOpen(true);
  };

  const openEditDrawer = (reminder) => {
    const scope = reminder?.scope || "GLOBAL";
    const allowedActionTypes = getAvailableActionTypes(scope);
    const actionType = allowedActionTypes.some(
      (item) => item.value === reminder?.action_type,
    )
      ? reminder?.action_type
      : getDefaultActionType(scope);
    const canAssignToCurrentAssignee = members.some(
      (member) =>
        !isOwnerMember(member) &&
        String(member.id) === String(reminder?.assignee),
    );
    setDrawerMode("edit");
    setEditingReminder(reminder);
    setForm({
      title: reminder?.title || "",
      action_type: actionType,
      selected_actions: [actionType],
      action_configs: {
        [actionType]: getDefaultActionConfig({
          frequency: reminder?.frequency || "DAILY",
          start_date: reminder?.start_date || getLocalDateKey(),
          custom_date: reminder?.custom_date || "",
          custom_days: (reminder?.custom_days || []).map((day) =>
            String(day).slice(0, 3).toUpperCase(),
          ),
          due_time: String(reminder?.due_time || "09:00").slice(0, 5),
          notes: reminder?.notes || "",
          status: reminder?.status || "ACTIVE",
        }),
      },
      reassignments: {},
      assignee: canAssignToCurrentAssignee ? reminder?.assignee || "" : "",
      scope,
      site: reminder?.site || "",
      sites:
        scope === "PROJECT" && reminder?.site ? [String(reminder.site)] : [],
      frequency: reminder?.frequency || "DAILY",
      start_date: reminder?.start_date || getLocalDateKey(),
      custom_date: reminder?.custom_date || "",
      custom_days: (reminder?.custom_days || []).map((day) =>
        String(day).slice(0, 3).toUpperCase(),
      ),
      due_time: String(reminder?.due_time || "09:00").slice(0, 5),
      notes: reminder?.notes || "",
      status: reminder?.status || "ACTIVE",
    });
    setDrawerStep(3);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditingReminder(null);
    setDrawerStep(1);
  };

  const submitReminder = async () => {
    if (!validateStep(4)) return;
    setSaving(true);
    try {
      const savePayload = async (payload, url, method) => {
        const response = await fetch(url, {
          method,
          headers: getAuthHeaders(),
          body: JSON.stringify(payload),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok || data?.status === false) {
          throw new Error(data?.message || "Failed to save action item");
        }
        return data;
      };

      const selectedActionTypes = getSelectedActionTypes(form);
      const selectedProjectId = getSelectedSiteIds(form)[0];

      if (drawerMode === "edit") {
        const actionType = selectedActionTypes[0] || form.action_type;
        await savePayload(
          buildPayload(
            form,
            selectedProjectId,
            actionType,
            getActionConfig(form, actionType),
          ),
          `${BASE_URL}/reminders/${editingReminder.id}/`,
          "PATCH",
        );
      } else {
        await Promise.all(
          selectedActionTypes.map((actionType) => {
            const payload = buildPayload(
              form,
              selectedProjectId,
              actionType,
              getActionConfig(form, actionType),
            );
            const reassignmentId = form.reassignments?.[actionType];
            return savePayload(
              payload,
              reassignmentId
                ? `${BASE_URL}/reminders/${reassignmentId}/`
                : `${BASE_URL}/reminders/org/${organizationId}/`,
              reassignmentId ? "PATCH" : "POST",
            );
          }),
        );
      }

      const reassignCount = selectedActionTypes.filter(
        (actionType) => form.reassignments?.[actionType],
      ).length;
      const createCount = selectedActionTypes.length - reassignCount;
      const successMessage =
        drawerMode === "edit"
          ? "Action item updated successfully"
          : [
              createCount
                ? `${createCount} action${createCount === 1 ? "" : "s"} created`
                : "",
              reassignCount
                ? `${reassignCount} action${reassignCount === 1 ? "" : "s"} reassigned`
                : "",
            ]
              .filter(Boolean)
              .join(" and ");

      addAlert(
        successMessage || "Action assignment saved successfully",
        "success",
      );
      closeDrawer();
      await fetchReminders();
      await fetchDateReminders(specificDateFilter);
      await fetchTodayItems(members);
    } catch (error) {
      addAlert(error?.message || "Failed to save action item", "error");
    } finally {
      setSaving(false);
    }
  };

  const updateReminderStatus = async (reminder, status) => {
    try {
      const response = await fetch(`${BASE_URL}/reminders/${reminder.id}/`, {
        method: "PATCH",
        headers: getAuthHeaders(),
        body: JSON.stringify({ status }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data?.status === false) {
        throw new Error(data?.message || "Failed to update status");
      }
      addAlert(`Action item ${status.toLowerCase()} successfully`, "success");
      await fetchReminders();
      await fetchDateReminders(specificDateFilter);
      await fetchTodayItems(members);
      return true;
    } catch (error) {
      addAlert(error?.message || "Failed to update status", "error");
      return false;
    }
  };

  const closeReminder = async (reminder) => {
    try {
      const response = await fetch(`${BASE_URL}/reminders/${reminder.id}/`, {
        method: "DELETE",
        headers: getAuthHeaders(),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data?.status === false) {
        throw new Error(data?.message || "Failed to close action item");
      }
      addAlert("Action item closed successfully", "success");
      await fetchReminders();
      await fetchDateReminders(specificDateFilter);
      await fetchTodayItems(members);
      return true;
    } catch (error) {
      addAlert(error?.message || "Failed to close action item", "error");
      return false;
    }
  };

  if (!organizationId) {
    return (
      <div className="flex h-full items-center justify-center bg-[#F9FAFC] px-6">
        <div className="max-w-md rounded-xl border border-slate-200 bg-white p-6 text-center shadow-sm">
          <AlertTriangle className="mx-auto mb-3 text-sm-accent" size={30} />
          <h2 className="text-lg font-bold text-slate-950">
            Organization not found
          </h2>
          <p className="mt-2 text-sm font-medium text-slate-500">
            Select an organization to manage action items.
          </p>
        </div>
      </div>
    );
  }

  if (!canManage) {
    return (
      <div className="flex h-full items-center justify-center bg-[#F9FAFC] px-6">
        <div className="max-w-md rounded-xl border border-slate-200 bg-white p-6 text-center shadow-sm">
          <AlertTriangle className="mx-auto mb-3 text-sm-accent" size={30} />
          <h2 className="text-lg font-bold text-slate-950">
            Admin access required
          </h2>
          <p className="mt-2 text-sm font-medium text-slate-500">
            Only organization owners and admins can assign or manage action
            items.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full px-10 pt-8 pb-12">
      <div className="mb-7 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-950">
            Action Items & Schedules
          </h1>
          <p className="text-sm font-medium text-slate-500">
            Set up tasks, payments, reports and reminders assigned across your
            team and projects.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            className="h-9 gap-2 bg-muted text-sm text-black hover:bg-gray-100"
            onClick={loadData}
            variant="outline"
          >
            <RefreshCcw size={14} />
            Refresh
          </Button>
          <Button
            className="h-9 gap-2 bg-sm-primary text-sm text-white hover:bg-sm-primary/90"
            onClick={openCreateDrawer}
          >
            <Plus size={14} />
            New Action Item
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-5 gap-4">
        <StatCard
          icon={RefreshCcw}
          value={stats.active}
          label="Active schedules"
          colorClass="bg-blue-50 text-blue-700"
        />
        <StatCard
          icon={Calendar}
          value={stats.dueToday}
          label="Due today"
          colorClass="bg-amber-50 text-amber-700"
        />
        <StatCard
          icon={AlertTriangle}
          value={stats.overdue}
          label="Overdue"
          colorClass="bg-red-50 text-red-700"
        />
        <StatCard
          icon={CheckCircle2}
          value={stats.completedToday}
          label="Completed today"
          colorClass="bg-green-50 text-green-700"
        />
        <StatCard
          icon={Pause}
          value={stats.paused}
          label="Paused"
          colorClass="bg-violet-50 text-violet-700"
        />
      </div>

      <div className="mt-6 flex flex-row flex-wrap items-center justify-between gap-2 border-y border-slate-100 py-3">
        <div className="flex flex-wrap items-center gap-3">
          <label className="relative inline-flex items-center rounded-lg border border-slate-200 bg-white p-2">
            <Calendar size={14} className="mr-3 shrink-0 text-slate-500" />
            <input
              type="date"
              value={specificDateFilter}
              onChange={(event) => setSpecificDateFilter(event.target.value)}
              className="w-full bg-transparent text-sm font-medium text-slate-700 outline-none"
            />
          </label>
          <DashboardSelect
            icon={Layers3}
            value={projectFilter}
            onChange={(event) => setProjectFilter(event.target.value)}
          >
            <option value="ALL">All projects ({projectOptions.length})</option>
            {projectOptions.map((project) => (
              <option key={project.value} value={project.value}>
                {project.label}
              </option>
            ))}
          </DashboardSelect>
          <DashboardSelect
            icon={Users}
            value={assigneeFilter}
            onChange={(event) => setAssigneeFilter(event.target.value)}
          >
            <option value="ALL">Everyone</option>
            {members
              .filter((member) => !isOwnerMember(member))
              .map((member) => (
                <option key={member.id} value={member.id}>
                  {member.displayName}
                </option>
              ))}
          </DashboardSelect>
          <DashboardSelect
            icon={ClipboardList}
            value={typeFilter}
            onChange={(event) => setTypeFilter(event.target.value)}
          >
            <option value="ALL">All actions</option>
            {ACTION_TYPES.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </DashboardSelect>
          <DashboardSelect
            icon={Filter}
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
          >
            <option value="ALL">Status</option>
            {Object.entries(statusMeta).map(([value, meta]) => (
              <option key={value} value={value}>
                {meta.label}
              </option>
            ))}
          </DashboardSelect>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs font-medium text-slate-500">
          {Object.entries(statusMeta).map(([value, meta]) => (
            <span key={value} className="inline-flex items-center gap-2">
              <span className={`h-2.5 w-2.5 rounded-full ${meta.dot}`} />
              {meta.label}
            </span>
          ))}
        </div>
      </div>

      <div className="mt-6 grid grid-cols-3 gap-3 2xl:grid-cols-[minmax(0,2.15fr)_minmax(360px,0.85fr)]">
        <OperationalFeed
          rows={filteredOperationalRows}
          loading={loading || dateRemindersLoading}
          selectedDate={specificDateFilter}
        />
        <SideInsights
          rows={filteredOperationalRows}
          selectedDate={specificDateFilter}
        />
      </div>

      <EmployeeCards
        employees={employeeOverview}
        onOpen={setSelectedEmployee}
      />
      <EmployeeMatrix
        rows={filteredOperationalRows}
        employees={employeeOverview}
      />

      <ReminderDrawer
        open={drawerOpen}
        mode={drawerMode}
        step={drawerStep}
        form={form}
        setForm={setForm}
        members={members}
        sites={sites}
        reminders={reminders}
        actionPermissionState={actionPermissionState}
        saving={saving}
        onClose={closeDrawer}
        onStep={handleStep}
        onSubmit={submitReminder}
      />
      <EmployeeDetailDrawer
        employee={selectedEmployee}
        members={members}
        reminders={reminders}
        selectedDate={specificDateFilter}
        onClose={() => setSelectedEmployee(null)}
        onEdit={(item) => {
          setSelectedEmployee(null);
          openEditDrawer(item);
        }}
        onCreate={(employee) => {
          setSelectedEmployee(null);
          openCreateDrawer(employee);
        }}
        onUpdateStatus={updateReminderStatus}
        onCloseReminder={closeReminder}
      />
    </div>
  );
}
